<template>
  <div class="entity-list-page database-connections-page">
    <header class="list-header">
      <div>
        <router-link to="/settings/platform" class="back-link">← Platform</router-link>
        <h1>Database connections</h1>
        <p class="list-sub">
          Connect ERP and warehouse databases so Build and compliance tools can query your business data.
        </p>
      </div>
      <button
        type="button"
        class="btn btn-primary"
        @click="showCreateModal = true"
      >
        <i class="fas fa-plus"></i>
        Add connection
      </button>
    </header>

    <nav class="page-tabs" aria-label="Database tools">
      <button
        v-for="tab in tabs"
        :key="tab.id"
        type="button"
        class="page-tab"
        :class="{ active: activeTab === tab.id }"
        @click="activeTab = tab.id"
      >
        <i :class="tab.icon" aria-hidden="true"></i>
        {{ tab.name }}
      </button>
    </nav>

    <!-- Tab Content -->
    <div class="tab-content">
      
      <!-- Connections Tab -->
      <div v-if="activeTab === 'connections'" class="tab-pane">
        <ul class="entity-card-grid" v-if="connections.length > 0">
          <li
            v-for="connection in connections"
            :key="connection.id"
            class="entity-card connection-card"
            :class="connectionCardClass(connection)"
          >
            <h3 class="entity-card-title">{{ connection.name }}</h3>
            <p class="entity-card-desc">
              {{ connection.description || 'No description' }}
            </p>
            <dl class="entity-card-meta">
              <div>
                <dt>Engine</dt>
                <dd>{{ connection.engine ? connection.engine.toUpperCase() : '—' }}</dd>
              </div>
              <div>
                <dt>Host</dt>
                <dd>{{ connection.host }}:{{ connection.port }}</dd>
              </div>
              <div>
                <dt>Database</dt>
                <dd>{{ connection.database_name }}</dd>
              </div>
              <div>
                <dt>Status</dt>
                <dd>{{ getStatusText(connection.status) }}</dd>
              </div>
              <template v-if="connection.status === 'ACTIVE'">
                <div>
                  <dt>Queries</dt>
                  <dd>{{ connection.query_count || 0 }}</dd>
                </div>
                <div>
                  <dt>Avg response</dt>
                  <dd>
                    {{ connection.avg_response_time ? Math.round(connection.avg_response_time) + 'ms' : '—' }}
                  </dd>
                </div>
                <div>
                  <dt>Last query</dt>
                  <dd>{{ connection.last_query_at ? formatDate(connection.last_query_at) : 'Never' }}</dd>
                </div>
              </template>
            </dl>

            <div class="entity-card-actions connection-actions">
              <button
                type="button"
                class="btn btn-primary"
                :disabled="testingConnections.includes(connection.id)"
                @click="testConnection(connection)"
              >
                <i
                  :class="testingConnections.includes(connection.id) ? 'fas fa-spinner fa-spin' : 'fas fa-plug'"
                  aria-hidden="true"
                ></i>
                Test
              </button>
              <button
                type="button"
                class="btn btn-ghost"
                @click="editConnection(connection)"
              >
                Edit
              </button>
              <div class="dropdown" :class="{ show: openDropdowns && openDropdowns.includes(connection.id) }">
                <button
                  type="button"
                  class="btn btn-ghost more-btn"
                  aria-label="More actions"
                  @click="toggleDropdown(connection.id)"
                >
                  <i class="fas fa-ellipsis-v" aria-hidden="true"></i>
                </button>
                <ul class="dropdown-menu" :class="{ show: openDropdowns && openDropdowns.includes(connection.id) }">
                  <li>
                    <a class="dropdown-item" @click="viewMappings(connection); closeDropdown(connection.id)">
                      <i class="fas fa-project-diagram mr-2"></i>Entity Mappings
                    </a>
                  </li>
                  <li>
                    <a class="dropdown-item" @click="viewHistory(connection); closeDropdown(connection.id)">
                      <i class="fas fa-history mr-2"></i>Query History
                    </a>
                  </li>
                  <li><hr class="dropdown-divider"></li>
                  <li>
                    <a class="dropdown-item text-danger" @click="deleteConnection(connection); closeDropdown(connection.id)">
                      <i class="fas fa-trash mr-2"></i>Delete
                    </a>
                  </li>
                </ul>
              </div>
            </div>
          </li>
        </ul>

        <div v-else class="state">
          No connections yet.
          <button type="button" class="linkish" @click="showCreateModal = true">
            Add a connection
          </button>
          <p class="empty-hint">
            Use Query Builder, Schema Explorer, and Duplicate Analysis after a connection is active.
          </p>
        </div>

    <!-- Create Connection Modal - Teleported to body for proper overlay -->
    <Teleport to="body">
      <div class="create-modal-overlay" v-if="showCreateModal" @click.self="showCreateModal = false">
        <div class="create-modal-container">
          <div class="create-modal-header">
            <h5 class="create-modal-title">
              <i class="fas fa-plus"></i>
              Add Database Connection
            </h5>
            <button 
              type="button" 
              class="create-modal-close" 
              @click="showCreateModal = false"
            >
              <i class="fas fa-times"></i>
            </button>
          </div>
          <div class="create-modal-body">
            <form @submit.prevent="createConnection">
              <!-- Connection Name and Engine Row -->
              <div class="create-form-row">
                <div class="create-form-group create-flex-2">
                  <label class="create-form-label">Connection Name <span class="create-required">*</span></label>
                  <input 
                    v-model="newConnection.name" 
                    type="text" 
                    class="create-form-input" 
                    placeholder="e.g., Production ERP Database"
                    required
                  />
                </div>
                <div class="create-form-group create-flex-1">
                  <label class="create-form-label">Database Engine <span class="create-required">*</span></label>
                  <select v-model="newConnection.engine" class="create-form-select" required>
                    <option value="">Select Engine</option>
                    <option value="mysql">MySQL</option>
                    <option value="postgresql">PostgreSQL</option>
                    <option value="mssql">SQL Server</option>
                    <option value="oracle">Oracle</option>
                  </select>
                </div>
              </div>
              
              <!-- Description -->
              <div class="create-form-group">
                <label class="create-form-label">Description</label>
                <textarea 
                  v-model="newConnection.description" 
                  class="create-form-textarea" 
                  rows="2"
                  placeholder="Brief description of this database and its purpose"
                ></textarea>
              </div>
              
              <!-- ERP Template Selection -->
              <div class="create-form-group">
                <label class="create-form-label">ERP System Template <span class="create-optional">(Optional)</span></label>
                <select v-model="newConnection.erp_template" class="create-form-select">
                  <option value="">Custom/Generic Database</option>
                  <option 
                    v-for="template in erpTemplates" 
                    :key="template.id" 
                    :value="template.id"
                  >
                    {{ template.name }}
                  </option>
                </select>
                <small class="create-form-hint">Select a template to enable smart entity mapping suggestions</small>
              </div>

              <!-- Host and Port Row -->
              <div class="create-form-row">
                <div class="create-form-group create-flex-2">
                  <label class="create-form-label">Host/Server <span class="create-required">*</span></label>
                  <input 
                    v-model="newConnection.host" 
                    type="text" 
                    class="create-form-input" 
                    placeholder="database.example.com"
                    required
                  />
                </div>
                <div class="create-form-group create-flex-1">
                  <label class="create-form-label">Port <span class="create-required">*</span></label>
                  <input 
                    v-model="newConnection.port" 
                    type="number" 
                    class="create-form-input" 
                    :placeholder="getDefaultPort(newConnection.engine)"
                    required
                  />
                </div>
              </div>

              <!-- Database Name and Username Row -->
              <div class="create-form-row">
                <div class="create-form-group create-flex-1">
                  <label class="create-form-label">Database Name <span class="create-required">*</span></label>
                  <input 
                    v-model="newConnection.database_name" 
                    type="text" 
                    class="create-form-input" 
                    placeholder="database_name"
                    required
                  />
                </div>
                <div class="create-form-group create-flex-1">
                  <label class="create-form-label">Username <span class="create-required">*</span></label>
                  <input 
                    v-model="newConnection.username" 
                    type="text" 
                    class="create-form-input" 
                    placeholder="database_user"
                    required
                  />
                </div>
              </div>

              <!-- Password -->
              <div class="create-form-group">
                <label class="create-form-label">Password <span class="create-required">*</span></label>
                <div class="create-password-group">
                  <input 
                    v-model="newConnection.password" 
                    :type="showPassword ? 'text' : 'password'" 
                    class="create-form-input create-password-input" 
                    placeholder="Enter database password"
                    required
                  />
                  <button 
                    class="create-password-toggle" 
                    type="button"
                    @click="showPassword = !showPassword"
                  >
                    <i :class="showPassword ? 'fas fa-eye-slash' : 'fas fa-eye'"></i>
                  </button>
                </div>
              </div>

              <!-- SSL Option -->
              <div class="create-form-group">
                <label class="create-checkbox-label">
                  <input 
                    v-model="newConnection.use_ssl" 
                    type="checkbox" 
                    class="create-checkbox"
                  />
                  <span class="create-checkbox-text">Use SSL/TLS encryption (recommended)</span>
                </label>
              </div>
            </form>
          </div>
          <div class="create-modal-footer">
            <button 
              type="button" 
              class="create-btn-cancel" 
              @click="showCreateModal = false"
            >
              Cancel
            </button>
            <button 
              type="button" 
              class="create-btn-save" 
              @click="createConnection"
              :disabled="creatingConnection"
            >
              <i class="fas fa-spinner fa-spin" v-if="creatingConnection"></i>
              <i class="fas fa-plus" v-else></i>
              {{ creatingConnection ? 'Creating...' : 'Create Connection' }}
            </button>
          </div>
        </div>
      </div>
    </Teleport>

    <!-- Edit Connection Modal - Teleported to body for proper overlay -->
    <Teleport to="body">
      <div class="edit-modal-overlay" v-if="showEditModal" @click.self="showEditModal = false">
        <div class="edit-modal-container">
          <div class="edit-modal-header">
            <h5 class="edit-modal-title">
              <i class="fas fa-edit"></i>
              Edit Database Connection
            </h5>
            <button 
              type="button" 
              class="edit-modal-close" 
              @click="showEditModal = false"
            >
              <i class="fas fa-times"></i>
            </button>
          </div>
          <div class="edit-modal-body">
            <form @submit.prevent="updateConnection">
              <!-- Connection Name and Engine Row -->
              <div class="edit-form-row">
                <div class="edit-form-group edit-flex-2">
                  <label class="edit-form-label">Connection Name <span class="edit-required">*</span></label>
                  <input 
                    v-model="editingConnection.name" 
                    type="text" 
                    class="edit-form-input" 
                    placeholder="e.g., Production ERP Database"
                    required
                  />
                </div>
                <div class="edit-form-group edit-flex-1">
                  <label class="edit-form-label">Database Engine <span class="edit-required">*</span></label>
                  <select v-model="editingConnection.engine" class="edit-form-select" required>
                    <option value="">Select Engine</option>
                    <option value="mysql">MySQL</option>
                    <option value="postgresql">PostgreSQL</option>
                    <option value="mssql">SQL Server</option>
                    <option value="oracle">Oracle</option>
                  </select>
                </div>
              </div>
              
              <!-- Description -->
              <div class="edit-form-group">
                <label class="edit-form-label">Description</label>
                <textarea 
                  v-model="editingConnection.description" 
                  class="edit-form-textarea" 
                  rows="2"
                  placeholder="Brief description of this database and its purpose"
                ></textarea>
              </div>
              
              <!-- ERP Template Selection -->
              <div class="edit-form-group">
                <label class="edit-form-label">ERP System Template <span class="edit-optional">(Optional)</span></label>
                <select v-model="editingConnection.erp_template" class="edit-form-select">
                  <option value="">Custom/Generic Database</option>
                  <option 
                    v-for="template in erpTemplates" 
                    :key="template.id" 
                    :value="template.id"
                  >
                    {{ template.name }}
                  </option>
                </select>
                <small class="edit-form-hint">Select a template to enable smart entity mapping suggestions</small>
              </div>

              <!-- Host and Port Row -->
              <div class="edit-form-row">
                <div class="edit-form-group edit-flex-2">
                  <label class="edit-form-label">Host/Server <span class="edit-required">*</span></label>
                  <input 
                    v-model="editingConnection.host" 
                    type="text" 
                    class="edit-form-input" 
                    placeholder="database.example.com"
                    required
                  />
                </div>
                <div class="edit-form-group edit-flex-1">
                  <label class="edit-form-label">Port <span class="edit-required">*</span></label>
                  <input 
                    v-model="editingConnection.port" 
                    type="number" 
                    class="edit-form-input" 
                    :placeholder="getDefaultPort(editingConnection.engine)"
                    required
                  />
                </div>
              </div>

              <!-- Database Name and Username Row -->
              <div class="edit-form-row">
                <div class="edit-form-group edit-flex-1">
                  <label class="edit-form-label">Database Name <span class="edit-required">*</span></label>
                  <input 
                    v-model="editingConnection.database_name" 
                    type="text" 
                    class="edit-form-input" 
                    placeholder="database_name"
                    required
                  />
                </div>
                <div class="edit-form-group edit-flex-1">
                  <label class="edit-form-label">Username <span class="edit-required">*</span></label>
                  <input 
                    v-model="editingConnection.username" 
                    type="text" 
                    class="edit-form-input" 
                    placeholder="database_user"
                    required
                  />
                </div>
              </div>

              <!-- Password -->
              <div class="edit-form-group">
                <label class="edit-form-label">
                  Password <span class="edit-optional">(leave blank to keep existing)</span>
                </label>
                <div class="edit-password-group">
                  <input 
                    v-model="editingConnection.password" 
                    :type="showPassword ? 'text' : 'password'" 
                    class="edit-form-input edit-password-input" 
                    placeholder="Enter new password to change"
                  />
                  <button 
                    class="edit-password-toggle" 
                    type="button"
                    @click="showPassword = !showPassword"
                  >
                    <i :class="showPassword ? 'fas fa-eye-slash' : 'fas fa-eye'"></i>
                  </button>
                </div>
              </div>

              <!-- SSL Option -->
              <div class="edit-form-group">
                <label class="edit-checkbox-label">
                  <input 
                    v-model="editingConnection.use_ssl" 
                    type="checkbox" 
                    class="edit-checkbox"
                  />
                  <span class="edit-checkbox-text">Use SSL/TLS encryption</span>
                </label>
              </div>
            </form>
          </div>
          <div class="edit-modal-footer">
            <button 
              type="button" 
              class="edit-btn-cancel" 
              @click="showEditModal = false"
            >
              Cancel
            </button>
            <button 
              type="button" 
              class="edit-btn-save" 
              @click="updateConnection"
              :disabled="updatingConnection"
            >
              <i class="fas fa-spinner fa-spin" v-if="updatingConnection"></i>
              <i class="fas fa-save" v-else></i>
              {{ updatingConnection ? 'Saving...' : 'Save Changes' }}
            </button>
          </div>
        </div>
      </div>
    </Teleport>

    <!-- Schema Suggestions Modal -->
    <div class="modal fade" id="schemaSuggestionsModal" tabindex="-1" v-if="showSchemaSuggestionsModal">
      <div class="modal-dialog modal-xl">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">
              <i class="fas fa-sitemap mr-2"></i>
              Schema Analysis: {{ selectedSchemaConnection?.name }}
            </h5>
            <button 
              type="button" 
              class="btn-close" 
              @click="showSchemaSuggestionsModal = false"
            ></button>
          </div>
          <div class="modal-body">
            <!-- Schema Overview -->
            <div class="row mb-4" v-if="schemaAnalysis">
              <div class="col-md-12">
                <div class="schema-overview">
                  <h6><i class="fas fa-database mr-2"></i>Database Overview</h6>
                  <div class="row">
                    <div class="col-md-3">
                      <div class="stat-card">
                        <div class="stat-value">{{ Object.keys(schemaAnalysis.tables || {}).length }}</div>
                        <div class="stat-label">Tables Discovered</div>
                      </div>
                    </div>
                    <div class="col-md-3">
                      <div class="stat-card">
                        <div class="stat-value">{{ Object.keys(schemaAnalysis.analysis?.suggested_entities || {}).length }}</div>
                        <div class="stat-label">Entity Suggestions</div>
                      </div>
                    </div>
                    <div class="col-md-3">
                      <div class="stat-card">
                        <div class="stat-value">{{ schemaAnalysis.analysis?.patterns_found?.length || 0 }}</div>
                        <div class="stat-label">Patterns Found</div>
                      </div>
                    </div>
                    <div class="col-md-3">
                      <div class="stat-card">
                        <div class="stat-value">{{ formatDate(schemaAnalysis.discovered_at) }}</div>
                        <div class="stat-label">Last Discovered</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Entity Suggestions -->
            <div class="mb-4" v-if="schemaAnalysis?.analysis?.suggested_entities">
              <h6><i class="fas fa-lightbulb mr-2"></i>Suggested Entity Mappings</h6>
              <div class="entity-suggestions">
                <div 
                  v-for="(entityType, tableName) in schemaAnalysis.analysis.suggested_entities" 
                  :key="tableName"
                  class="suggestion-card"
                >
                  <div class="suggestion-header">
                    <div class="suggestion-info">
                      <h6 class="suggestion-table">{{ tableName }}</h6>
                      <span class="suggestion-type badge bg-primary">{{ entityType }}</span>
                      <span class="confidence-score">
                        Confidence: {{ Math.round((schemaAnalysis.analysis.confidence_scores?.[tableName] || 0) * 100) }}%
                      </span>
                    </div>
                    <div class="suggestion-actions">
                      <button 
                        class="btn btn-sm btn-success"
                        @click="createEntityMapping(tableName, entityType)"
                      >
                        <i class="fas fa-plus mr-1"></i>Create Mapping
                      </button>
                      <button 
                        class="btn btn-sm btn-primary"
                        @click="generateAIQuery(tableName, entityType)"
                        :disabled="generatingAIQuery"
                      >
                        <i class="fas fa-magic mr-1" v-if="!generatingAIQuery"></i>
                        <i class="fas fa-spinner fa-spin mr-1" v-else></i>
                        AI Query
                      </button>
                    </div>
                  </div>
                  <div class="suggestion-details">
                    <div class="table-columns">
                      <strong>Columns:</strong>
                      <div class="columns-list">
                        <span 
                          v-for="column in getTableColumns(tableName)"
                          :key="column.name"
                          class="column-badge"
                          :class="{ 'primary-key': column.primary_key }"
                        >
                          {{ column.name }}
                          <small>({{ column.type }})</small>
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- All Tables -->
            <div class="mb-4" v-if="schemaAnalysis?.tables">
              <h6><i class="fas fa-table mr-2"></i>All Discovered Tables</h6>
              <div class="tables-grid">
                <div 
                  v-for="(tableInfo, tableName) in schemaAnalysis.tables"
                  :key="tableName"
                  class="table-card"
                >
                  <div class="table-header">
                    <h6 class="table-name">{{ tableName }}</h6>
                    <div class="table-stats">
                      <span class="badge bg-secondary">{{ tableInfo.columns?.length || 0 }} columns</span>
                      <span class="badge bg-info" v-if="tableInfo.estimated_rows">{{ tableInfo.estimated_rows }} rows</span>
                    </div>
                  </div>
                  <div class="table-actions">
                    <button 
                      class="btn btn-sm btn-outline-primary"
                      @click="previewTable(tableName)"
                    >
                      <i class="fas fa-eye mr-1"></i>Preview
                    </button>
                    <button 
                      class="btn btn-sm btn-outline-success"
                      @click="queryTable(tableName)"
                    >
                      <i class="fas fa-play mr-1"></i>Query
                    </button>
                    <button 
                      class="btn btn-sm btn-outline-info"
                      @click="generateTableAIQuery(tableName)"
                      :disabled="generatingAIQuery"
                    >
                      <i class="fas fa-magic mr-1" v-if="!generatingAIQuery"></i>
                      <i class="fas fa-spinner fa-spin mr-1" v-else></i>
                      AI
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <!-- Patterns Found -->
            <div v-if="schemaAnalysis?.analysis?.patterns_found?.length">
              <h6><i class="fas fa-search mr-2"></i>Analysis Patterns</h6>
              <div class="patterns-list">
                <div 
                  v-for="(pattern, index) in schemaAnalysis.analysis.patterns_found"
                  :key="index"
                  class="pattern-item"
                >
                  <i class="fas fa-check-circle text-success mr-2"></i>
                  {{ pattern }}
                </div>
              </div>
            </div>
          </div>
          <div class="modal-footer">
            <button 
              type="button" 
              class="btn btn-secondary" 
              @click="showSchemaSuggestionsModal = false"
            >
              Close
            </button>
            <button 
              type="button" 
              class="btn btn-primary" 
              @click="refreshSchema"
              :disabled="discoveringSchema.includes(selectedSchemaConnection?.id)"
            >
              <i class="fas fa-refresh mr-2"></i>
              Refresh Schema
            </button>
          </div>
        </div>
      </div>
    </div> <!-- End Connections Tab Modal -->
    </div> <!-- End Connections Tab Pane -->

    <!-- Knowledge Base Tab -->
      <div v-if="activeTab === 'knowledge-base'" class="tab-pane">
        <div class="knowledge-base-section">
          <div class="section-header-row mb-4">
            <div>
              <h3><i class="fas fa-brain mr-2 text-primary"></i>Logical Schema Knowledge Base</h3>
              <p class="text-muted">Use Data Dictionaries and Field Cross-Reference documents to teach the agent about your database's logical structure, cryptic aliases, and implicit relationships.</p>
            </div>
          </div>

          <div v-if="connections.length === 0" class="alert alert-info">
            <i class="fas fa-info-circle mr-2"></i>
            Please add a database connection first to build its knowledge base.
          </div>

          <div v-else class="knowledge-interface">
            <div class="row">
              <!-- Left Panel: Document Association -->
              <div class="col-md-4">
                <div class="card shadow-sm mb-4 border-0 rounded-lg overflow-hidden">
                  <div class="card-header bg-light border-0 py-3">
                    <h5 class="mb-0 text-primary font-weight-bold"><i class="fas fa-plus-circle mr-2"></i>Add Context</h5>
                  </div>
                  <div class="card-body bg-white">
                    <div class="mb-4">
                      <label class="form-label font-weight-bold small text-uppercase text-muted">1. Select Database</label>
                      <select v-model="activeKnowledgeConnection" class="form-select">
                        <option :value="null">Select a connection...</option>
                        <option 
                          v-for="connection in connections" 
                          :key="connection.id" 
                          :value="connection"
                        >
                          {{ connection.name }}
                        </option>
                      </select>
                    </div>

                    <div v-if="activeKnowledgeConnection">
                      <div class="mb-4">
                        <label class="form-label font-weight-bold small text-uppercase text-muted">2. Upload Reference Document</label>
                        
                        <div 
                          class="upload-zone p-4 border-2 border-dashed rounded text-center bg-light-soft mb-3"
                          :class="{ 'opacity-50': uploadingDocument }"
                          @click="!uploadingDocument && fileInput.click()"
                          style="cursor: pointer;"
                        >
                          <div v-if="uploadingDocument">
                            <i class="fas fa-spinner fa-spin fa-2x mb-2 text-primary"></i>
                            <p class="mb-0 small">Analyzing document...</p>
                          </div>
                          <div v-else>
                            <i class="fas fa-upload fa-2x mb-2 text-muted"></i>
                            <p class="mb-0 small font-weight-bold">Click to upload Data Dictionary or ERD</p>
                            <p class="text-xs text-muted mt-1">(PDF, TXT, or Word)</p>
                          </div>
                          <input 
                            type="file" 
                            ref="fileInput" 
                            style="display: none" 
                            @change="handleFileUpload" 
                            accept=".pdf,.doc,.docx,.txt"
                          >
                        </div>

                        <div class="form-text small mt-2">
                          <i class="fas fa-info-circle mr-1"></i> Documents uploaded here are strictly isolated to this connection.
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Associated Documents List -->
                <div v-if="activeKnowledgeConnection && activeKnowledgeConnection.references?.length > 0" class="card shadow-sm border-0 rounded-lg overflow-hidden">
                  <div class="card-header bg-light border-0 py-3 d-flex justify-content-between align-items-center">
                    <h5 class="mb-0 text-primary font-weight-bold">Stored References</h5>
                    <button @click="clearLogicalSchema" class="btn btn-sm btn-outline-danger border-0" title="Clear All Knowledge">
                      <i class="fas fa-trash-alt"></i>
                    </button>
                  </div>
                  <div class="list-group list-group-flush">
                    <div 
                      v-for="doc in activeKnowledgeConnection.references" 
                      :key="doc.id" 
                      class="list-group-item d-flex align-items-center py-3"
                    >
                      <div class="bg-danger-light p-2 rounded mr-3">
                        <i class="fas fa-file-pdf text-danger fa-lg"></i>
                      </div>
                      <div class="flex-grow-1 overflow-hidden">
                        <div class="text-truncate font-weight-bold">{{ doc.name }}</div>
                        <small class="text-muted">Uploaded {{ new Date(doc.created_at).toLocaleDateString() }}</small>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Right Panel: Learned Knowledge Display -->
              <div class="col-md-8">
                <div class="card shadow-sm h-100 border-0 rounded-lg overflow-hidden">
                  <div class="card-header bg-white border-0 py-3 d-flex justify-content-between align-items-center shadow-sm">
                    <h5 class="mb-0 text-primary font-weight-bold">Agent's Logical Understanding</h5>
                    <span v-if="activeKnowledgeConnection && activeKnowledgeConnection.logical_schema_context?.tables" class="badge bg-primary-soft text-primary px-3 py-2 rounded-pill">
                      {{ Object.keys(activeKnowledgeConnection.logical_schema_context.tables).length }} Tables Learned
                    </span>
                  </div>
                  <div class="card-body bg-light p-0 overflow-auto" style="max-height: 800px;">
                    <div v-if="!activeKnowledgeConnection" class="p-5 text-center text-muted h-100 d-flex flex-column justify-content-center">
                      <i class="fas fa-database fa-4x mb-4 opacity-10"></i>
                      <h4 class="font-weight-light">Select a connection to view learned knowledge.</h4>
                    </div>

                    <div v-else-if="!activeKnowledgeConnection.logical_schema_context?.tables && !activeKnowledgeConnection.logical_schema_context?.general_notes" class="p-5 text-center text-muted h-100 d-flex flex-column justify-content-center">
                      <i class="fas fa-brain fa-4x mb-4 opacity-10"></i>
                      <h4 class="font-weight-light">No knowledge base for this connection yet.</h4>
                      <p class="mx-auto" style="max-width: 400px;">Use the panel on the left to teach the agent about this database using your Data Dictionary documents.</p>
                    </div>

                    <div v-else class="knowledge-content p-4">
                      <!-- Knowledge Summary Card -->
                      <div class="knowledge-summary-card mb-4">
                        <div class="d-flex align-items-center">
                          <div class="knowledge-icon-wrapper">
                            <i class="fas fa-brain"></i>
                          </div>
                          <div class="flex-grow-1">
                            <h5 class="mb-1 font-weight-bold text-dark">Agent Has Schema Context</h5>
                            <p class="mb-0 text-muted small">This database's structure is understood and ready for intelligent queries</p>
                          </div>
                          <div class="knowledge-stats text-right">
                            <div class="stat-item" v-if="activeKnowledgeConnection.logical_schema_context.tables">
                              <span class="stat-value">{{ Object.keys(activeKnowledgeConnection.logical_schema_context.tables).length }}</span>
                              <span class="stat-label">Tables</span>
                            </div>
                          </div>
                        </div>
                        
                        <!-- Extraction Info -->
                        <div class="extraction-info mt-3 pt-3 border-top" v-if="activeKnowledgeConnection.logical_schema_context._extraction_info">
                          <div class="d-flex align-items-center text-muted small">
                            <i class="fas fa-file-alt mr-2"></i>
                            <span>{{ activeKnowledgeConnection.logical_schema_context._extraction_info.document }}</span>
                            <span class="mx-2">•</span>
                            <span>{{ activeKnowledgeConnection.logical_schema_context._extraction_info.pages_processed }} pages processed</span>
                          </div>
                        </div>
                      </div>

                      <!-- Global Schema Rules (Collapsible) -->
                      <div v-if="activeKnowledgeConnection.logical_schema_context.general_notes" class="mb-4">
                        <div 
                          class="d-flex align-items-center mb-2 cursor-pointer" 
                          @click="showSchemaRules = !showSchemaRules"
                        >
                          <i class="fas fa-info-circle mr-2 text-info"></i>
                          <h6 class="mb-0 font-weight-bold text-uppercase small text-muted">Global Schema Rules</h6>
                          <i :class="showSchemaRules ? 'fas fa-chevron-up' : 'fas fa-chevron-down'" class="ml-auto text-muted"></i>
                        </div>
                        <div v-if="showSchemaRules" class="bg-white p-4 rounded-lg shadow-sm border-left border-info border-3">
                          <p class="mb-0 whitespace-pre-wrap small text-dark line-height-base">{{ activeKnowledgeConnection.logical_schema_context.general_notes }}</p>
                        </div>
                      </div>

                      <!-- Table Mappings (Collapsible with Search) -->
                      <div v-if="activeKnowledgeConnection.logical_schema_context.tables">
                        <div 
                          class="d-flex align-items-center mb-3 cursor-pointer"
                          @click="showTableDetails = !showTableDetails"
                        >
                          <i class="fas fa-table mr-2 text-success"></i>
                          <h6 class="mb-0 font-weight-bold text-uppercase small text-muted">
                            Learned Tables & Aliases 
                            <span class="badge bg-secondary ml-2">{{ Object.keys(activeKnowledgeConnection.logical_schema_context.tables).length }}</span>
                          </h6>
                          <i :class="showTableDetails ? 'fas fa-chevron-up' : 'fas fa-chevron-down'" class="ml-auto text-muted"></i>
                        </div>
                        
                        <div v-if="showTableDetails">
                          <!-- Search Filter -->
                          <div class="mb-3">
                            <input 
                              v-model="tableSearchQuery" 
                              type="text" 
                              class="form-control form-control-sm" 
                              placeholder="Search tables..."
                            />
                          </div>
                          
                          <div class="table-knowledge-list" style="max-height: 500px; overflow-y: auto;">
                            <div 
                              v-for="(table, tableName) in filteredTables" 
                              :key="tableName"
                              class="card mb-3 border-0 shadow-sm rounded-lg overflow-hidden"
                            >
                              <div 
                                class="card-header bg-white border-0 py-3 d-flex align-items-center cursor-pointer"
                                @click="toggleTableExpand(tableName)"
                              >
                                <span class="badge bg-dark px-3 py-2 rounded">{{ tableName }}</span>
                                <i class="fas fa-long-arrow-alt-right mx-3 text-muted"></i>
                                <span class="h6 mb-0 font-weight-bold text-primary">{{ table.alias }}</span>
                                <span v-if="table.entity_type" class="badge bg-success-soft text-success ml-2 px-2 py-1 rounded-pill small">{{ table.entity_type.toUpperCase() }}</span>
                                <i :class="expandedTables.includes(tableName) ? 'fas fa-chevron-up' : 'fas fa-chevron-down'" class="ml-auto text-muted"></i>
                              </div>
                              
                              <div v-if="expandedTables.includes(tableName)" class="card-body bg-white border-top border-light">
                                <p class="text-muted small mb-4 line-height-base">{{ table.description }}</p>
                                
                                <div class="row">
                                  <!-- Columns -->
                                  <div class="col-lg-7" v-if="table.columns && Object.keys(table.columns).length > 0">
                                    <div class="text-uppercase small font-weight-bold text-muted mb-2 ls-1">Column Meanings</div>
                                    <div class="table-responsive rounded border">
                                      <table class="table table-sm table-hover mb-0 small">
                                        <thead class="bg-light border-0">
                                          <tr>
                                            <th class="border-0 px-3">Physical</th>
                                            <th class="border-0">Alias</th>
                                            <th class="border-0">Narrative</th>
                                          </tr>
                                        </thead>
                                        <tbody>
                                          <tr v-for="(col, colName) in table.columns" :key="colName">
                                            <td class="px-3"><code>{{ colName }}</code></td>
                                            <td class="font-weight-bold">{{ col.alias }}</td>
                                            <td class="text-muted">{{ col.description }}</td>
                                          </tr>
                                        </tbody>
                                      </table>
                                    </div>
                                  </div>

                                  <!-- Relationships -->
                                  <div class="col-lg-5 mt-4 mt-lg-0" v-if="table.relationships && table.relationships.length > 0">
                                    <div class="text-uppercase small font-weight-bold text-muted mb-2 ls-1">Implicit Relationships</div>
                                    <div class="relationship-list">
                                      <div v-for="(rel, idx) in table.relationships" :key="idx" class="relationship-badge p-2 mb-2 bg-light rounded border-left border-primary border-3">
                                        <div class="d-flex align-items-center mb-1">
                                          <code>{{ rel.from_column }}</code>
                                          <i class="fas fa-link mx-2 text-primary small"></i>
                                          <span class="badge bg-white border text-dark">{{ rel.to_table }}.{{ rel.to_column }}</span>
                                        </div>
                                        <div class="small text-muted italic ml-1 mt-1">{{ rel.description }}</div>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Query Builder Tab -->
      <div v-if="activeTab === 'query-builder'" class="tab-pane">
        <div class="query-builder-section">
          <h3><i class="fas fa-code mr-2"></i>SQL Query Builder</h3>
          <p>Build and execute custom SQL queries against your connected databases.</p>
          
          <div v-if="connections.length === 0" class="alert alert-info">
            <i class="fas fa-info-circle mr-2"></i>
            Please add a database connection first to use the query builder.
          </div>
          
          <div v-else class="query-interface">
            <!-- Connection Selector -->
            <div class="connection-selector mb-4">
              <label class="form-label">Select Database Connection:</label>
              <select v-model="selectedConnection" class="form-select">
                <option value="">Choose a connection...</option>
                <option v-for="connection in connections" :key="connection.id" :value="connection">
                  {{ connection.name }} ({{ connection.engine ? connection.engine.toUpperCase() : 'UNKNOWN' }})
                </option>
              </select>
            </div>
            
            <!-- Query Interface -->
            <div v-if="selectedConnection" class="query-form-section">
              <div class="row">
                <div class="col-md-12 mb-3">
                  <label class="form-label">SQL Query</label>
                  <textarea 
                    v-model="queryForm.query" 
                    class="form-control font-monospace" 
                    rows="8"
                    placeholder="SELECT * FROM your_table LIMIT 10;"
                  ></textarea>
                  <div class="form-text">
                    Only SELECT queries are allowed. Use LIMIT to avoid large result sets.
                    <span v-if="lastAIGeneration" class="text-success ml-2">
                      <i class="fas fa-check mr-1"></i>Generated by AI ({{ lastAIGeneration.provider }}, {{ Math.round(lastAIGeneration.confidence * 100) }}% confidence)
                    </span>
                  </div>
                </div>
                <div class="col-md-6 mb-3">
                  <label class="form-label">Query Purpose</label>
                  <select v-model="queryForm.purpose" class="form-select">
                    <option value="manual_query">Manual Query</option>
                    <option value="vendor_lookup">Vendor Lookup</option>
                    <option value="compliance_check">Compliance Check</option>
                    <option value="data_exploration">Data Exploration</option>
                    <option value="entity_sync">Entity Sync</option>
                  </select>
                </div>
                <div class="col-md-6 mb-3">
                  <button 
                    @click="executeQuery"
                    class="btn btn-primary w-100"
                    :disabled="!queryForm.query.trim() || executingQuery"
                  >
                    <i class="fas fa-spinner fa-spin mr-2" v-if="executingQuery"></i>
                    <i class="fas fa-play mr-2" v-else></i>
                    Execute Query
                  </button>
                </div>
              </div>
            </div>
            
            <!-- Query Results -->
            <div v-if="queryResults" class="query-results mt-4">
              <div class="results-header d-flex justify-content-between align-items-center mb-3">
                <h6 class="mb-0">
                  <i class="fas fa-table mr-2"></i>Query Results
                </h6>
                <div class="results-actions">
                  <button @click="exportToCSV" class="btn btn-sm btn-outline-primary me-2">
                    <i class="fas fa-download mr-1"></i>CSV
                  </button>
                  <button @click="exportToJSON" class="btn btn-sm btn-outline-primary">
                    <i class="fas fa-download mr-1"></i>JSON
                  </button>
                </div>
              </div>
              
              <!-- Query KPIs -->
              <div class="query-kpis mb-3">
                <div class="kpi-item">
                  <span class="kpi-value">{{ queryResults.row_count }}</span>
                  <span class="kpi-label">Rows</span>
                </div>
                <div class="kpi-divider"></div>
                <div class="kpi-item">
                  <span class="kpi-value">{{ queryResults.columns?.length || 0 }}</span>
                  <span class="kpi-label">Columns</span>
                </div>
                <div class="kpi-divider"></div>
                <div class="kpi-item">
                  <span class="kpi-value">{{ queryResults.execution_time_ms }}ms</span>
                  <span class="kpi-label">Time</span>
                </div>
              </div>
              
              <!-- Results Table -->
              <div class="table-responsive">
                <table class="table table-striped table-hover">
                  <thead class="table-dark">
                    <tr>
                      <th v-for="column in queryResults.columns" :key="column">{{ column }}</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(row, index) in queryResults.data" :key="index">
                      <td v-for="(value, colIndex) in row" :key="colIndex" class="table-cell">
                        {{ formatCellValue(value) }}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <!-- Duplicate Analysis Tab -->
      <div v-if="activeTab === 'duplicate-analysis'" class="tab-pane">
        <div class="duplicate-analysis-section">
          <h3><i class="fas fa-search-plus mr-2"></i>Duplicate Detection</h3>
          <p>AI-powered analysis to detect duplicate work orders, purchase orders, and potential cost overruns.</p>
          
          <div v-if="connections.length === 0" class="alert alert-info">
            <i class="fas fa-info-circle mr-2"></i>
            Please add a database connection first to use duplicate analysis.
          </div>
          
          <div v-else class="duplicate-interface">
            <!-- Connection Selector -->
            <div class="connection-selector mb-4">
              <label class="form-label">Select Database Connection:</label>
              <select v-model="selectedConnection" class="form-select">
                <option value="">Choose a connection...</option>
                <option v-for="connection in connections" :key="connection.id" :value="connection">
                  {{ connection.name }} ({{ connection.engine ? connection.engine.toUpperCase() : 'UNKNOWN' }})
                </option>
              </select>
            </div>
            
            <!-- Duplicate Analysis Form -->
            <div v-if="selectedConnection" class="duplicate-form-section">
              <div class="duplicate-form">
                <div class="row">
                  <div class="col-md-4">
                    <label class="form-label">House Number:</label>
                    <input 
                      v-model="duplicateForm.houseNumber"
                      type="text" 
                      class="form-control"
                      placeholder="Enter house number (e.g., 50000555)"
                    />
                  </div>
                  <div class="col-md-4">
                    <label class="form-label">Analysis Focus:</label>
                    <select v-model="duplicateForm.analysisFocus" class="form-select">
                      <option value="general">General Duplicates</option>
                      <option value="cost_overruns">Cost Overruns</option>
                      <option value="vendor_conflicts">Vendor Conflicts</option>
                      <option value="timeline_issues">Timeline Issues</option>
                    </select>
                  </div>
                  <div class="col-md-4">
                    <label class="form-label">&nbsp;</label>
                    <button 
                      @click="analyzeDuplicates"
                      class="btn btn-primary w-100"
                      :disabled="!duplicateForm.houseNumber || analyzingDuplicates"
                    >
                      <i class="fas fa-brain mr-2"></i>
                      <span v-if="analyzingDuplicates">Analyzing...</span>
                      <span v-else>Analyze Duplicates</span>
                    </button>
                  </div>
                </div>
                <small class="form-text text-muted mt-2">
                  AI will analyze all work orders and purchase orders for this house to detect potential duplicates
                </small>
              </div>
            </div>
            
            <!-- Duplicate Analysis Results -->
            <div v-if="duplicateResults" class="duplicate-results mt-4">
              <div class="results-header">
                <h5><i class="fas fa-brain mr-2"></i>Duplicate Analysis Results</h5>
                <span class="badge bg-info">House {{ duplicateResults.house_number }}</span>
              </div>
              
              <div v-if="duplicateResults.success" class="analysis-content">
                <!-- Summary Cards -->
                <div class="duplicate-summary mb-3">
                  <div class="summary-card">
                    <div class="summary-value">{{ duplicateResults.structured_results?.duplicates_found || 0 }}</div>
                    <div class="summary-label">Potential Duplicates</div>
                  </div>
                  <div class="summary-card">
                    <div class="summary-value">{{ duplicateResults.data_summary?.total_records || 0 }}</div>
                    <div class="summary-label">Records Analyzed</div>
                  </div>
                  <div class="summary-card">
                    <div class="summary-value">{{ duplicateResults.execution_time_ms }}ms</div>
                    <div class="summary-label">Analysis Time</div>
                  </div>
                </div>
                
                <!-- High Confidence Duplicates -->
                <div v-if="getHighConfidenceDuplicates().length" class="duplicates-section">
                  <h6 class="duplicates-title">
                    <i class="fas fa-exclamation-triangle text-danger mr-2"></i>
                    High Confidence Duplicates ({{ getHighConfidenceDuplicates().length }})
                  </h6>
                  
                  <div class="duplicates-list">
                    <div 
                      v-for="duplicate in getHighConfidenceDuplicates()" 
                      :key="duplicate.duplicate_id || Math.random()"
                      class="duplicate-item high-confidence"
                    >
                      <div class="duplicate-header">
                        <div class="duplicate-title">
                          <h6>{{ duplicate.title || 'Potential duplicate detected' }}</h6>
                          <span class="duplicate-type-badge" :class="duplicate.type">{{ formatDuplicateType(duplicate.type) }}</span>
                        </div>
                        <div class="duplicate-meta">
                          <span class="confidence-badge high">{{ Math.round((duplicate.confidence || 0.5) * 100) }}% confidence</span>
                          <span v-if="duplicate.cost_impact" class="cost-impact">
                            ${{ duplicate.cost_impact.toLocaleString() }} impact
                          </span>
                        </div>
                      </div>
                      
                      <!-- Records Comparison -->
                      <div class="records-comparison">
                        <h6>Records Involved:</h6>
                        <div class="records-grid">
                          <div v-for="(record, index) in duplicate.records_involved" :key="index" class="record-card">
                            <div class="record-header">
                              <span class="table-name">{{ record.table }}</span>
                              <span class="record-id">{{ record.record_id }}</span>
                            </div>
                            <div class="record-details">
                              <div v-for="(value, field) in record.key_fields" :key="field" class="field-row">
                                <span class="field-name">{{ field }}:</span>
                                <span class="field-value">{{ value }}</span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                      
                      <!-- Detailed Reasoning -->
                      <div v-if="duplicate.detailed_reasoning" class="detailed-reasoning">
                        <h6>Analysis Details:</h6>
                        <div class="reasoning-content">
                          <div v-if="duplicate.detailed_reasoning.similarity_analysis" class="reasoning-item">
                            <strong>Similarity:</strong> {{ duplicate.detailed_reasoning.similarity_analysis }}
                          </div>
                          <div v-if="duplicate.detailed_reasoning.differences_found" class="reasoning-item">
                            <strong>Differences:</strong> {{ duplicate.detailed_reasoning.differences_found }}
                          </div>
                          <div v-if="duplicate.detailed_reasoning.timeline_analysis" class="reasoning-item">
                            <strong>Timeline:</strong> {{ duplicate.detailed_reasoning.timeline_analysis }}
                          </div>
                          <div v-if="duplicate.detailed_reasoning.duplicate_indicators" class="reasoning-item">
                            <strong>Duplicate Indicators:</strong>
                            <ul class="indicator-list">
                              <li v-for="indicator in duplicate.detailed_reasoning.duplicate_indicators" :key="indicator">
                                {{ indicator }}
                              </li>
                            </ul>
                          </div>
                          <div v-if="duplicate.detailed_reasoning.risk_factors" class="reasoning-item">
                            <strong>Risk Factors:</strong>
                            <ul class="risk-list">
                              <li v-for="risk in duplicate.detailed_reasoning.risk_factors" :key="risk">
                                {{ risk }}
                              </li>
                            </ul>
                          </div>
                        </div>
                      </div>
                      
                      <!-- Validation Questions -->
                      <div v-if="duplicate.validation_questions?.length" class="validation-questions">
                        <h6>Validation Questions:</h6>
                        <div class="questions-list">
                          <div v-for="(question, qIndex) in duplicate.validation_questions" :key="qIndex" class="question-item">
                            <i class="fas fa-question-circle text-info mr-2"></i>
                            {{ question }}
                          </div>
                        </div>
                      </div>
                      
                      <!-- User Validation Interface -->
                      <div class="user-validation">
                        <h6>Your Assessment:</h6>
                        <div class="validation-controls">
                          <div class="validation-buttons">
                            <button 
                              @click="validateDuplicate(duplicate.duplicate_id, 'confirmed')"
                              class="btn btn-sm btn-danger"
                              :disabled="duplicate.user_validation === 'confirmed'"
                            >
                              <i class="fas fa-check mr-1"></i>
                              Confirmed Duplicate
                            </button>
                            <button 
                              @click="validateDuplicate(duplicate.duplicate_id, 'false_positive')"
                              class="btn btn-sm btn-success"
                              :disabled="duplicate.user_validation === 'false_positive'"
                            >
                              <i class="fas fa-times mr-1"></i>
                              Not a Duplicate
                            </button>
                            <button 
                              @click="validateDuplicate(duplicate.duplicate_id, 'needs_review')"
                              class="btn btn-sm btn-warning"
                              :disabled="duplicate.user_validation === 'needs_review'"
                            >
                              <i class="fas fa-eye mr-1"></i>
                              Needs Review
                            </button>
                          </div>
                          
                          <div class="validation-notes mt-2">
                            <textarea 
                              v-model="duplicate.validation_notes"
                              @blur="saveValidationNotes(duplicate.duplicate_id, duplicate.validation_notes)"
                              class="form-control form-control-sm"
                              placeholder="Add notes about this duplicate..."
                              rows="2"
                            ></textarea>
                          </div>
                          
                          <div v-if="duplicate.user_validation" class="validation-status mt-2">
                            <span class="badge" :class="{
                              'bg-danger': duplicate.user_validation === 'confirmed',
                              'bg-success': duplicate.user_validation === 'false_positive', 
                              'bg-warning': duplicate.user_validation === 'needs_review'
                            }">
                              Status: {{ duplicate.user_validation ? String(duplicate.user_validation).replace('_', ' ').toUpperCase() : 'PENDING' }}
                            </span>
                          </div>
                        </div>
                      </div>
                      
                      <!-- Recommended Action -->
                      <div v-if="duplicate.recommended_action" class="recommended-action">
                        <h6>Recommended Action:</h6>
                        <div class="action-content">
                          <i class="fas fa-tasks text-primary mr-2"></i>
                          {{ duplicate.recommended_action }}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                
                <!-- Medium Confidence Duplicates -->
                <div v-if="getMediumConfidenceDuplicates().length" class="duplicates-section">
                  <h6 class="duplicates-title">
                    <i class="fas fa-exclamation-circle text-warning mr-2"></i>
                    Medium Confidence Duplicates ({{ getMediumConfidenceDuplicates().length }})
                  </h6>
                  
                  <div class="duplicates-list">
                    <div 
                      v-for="duplicate in getMediumConfidenceDuplicates()" 
                      :key="duplicate.duplicate_id || Math.random()"
                      class="duplicate-item medium-confidence"
                    >
                      <!-- Same structure as high confidence duplicates -->
                      <div class="duplicate-header">
                        <div class="duplicate-title">
                          <h6>{{ duplicate.title || 'Potential duplicate detected' }}</h6>
                          <span class="duplicate-type-badge" :class="duplicate.type">{{ formatDuplicateType(duplicate.type) }}</span>
                        </div>
                        <div class="duplicate-meta">
                          <span class="confidence-badge medium">{{ Math.round((duplicate.confidence || 0.5) * 100) }}% confidence</span>
                          <span v-if="duplicate.cost_impact" class="cost-impact">
                            ${{ duplicate.cost_impact.toLocaleString() }} impact
                          </span>
                        </div>
                      </div>
                      
                      <!-- Records, reasoning, validation - same as high confidence -->
                      <div class="records-comparison">
                        <h6>Records Involved:</h6>
                        <div class="records-grid">
                          <div v-for="(record, index) in duplicate.records_involved" :key="index" class="record-card">
                            <div class="record-header">
                              <span class="table-name">{{ record.table }}</span>
                              <span class="record-id">{{ record.record_id }}</span>
                            </div>
                            <div class="record-details">
                              <div v-for="(value, field) in record.key_fields" :key="field" class="field-row">
                                <span class="field-name">{{ field }}:</span>
                                <span class="field-value">{{ value }}</span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                
                <!-- Recommendations -->
                <div v-if="duplicateResults.structured_results?.recommendations?.length" class="recommendations-section">
                  <h6><i class="fas fa-lightbulb text-warning mr-2"></i>Recommendations</h6>
                  <ul class="recommendations-list">
                    <li v-for="(recommendation, index) in duplicateResults.structured_results.recommendations" :key="index">
                      {{ recommendation }}
                    </li>
                  </ul>
                </div>
              </div>
              
              <div v-else class="alert alert-danger">
                <i class="fas fa-exclamation-triangle mr-2"></i>
                Analysis failed: {{ duplicateResults.error || 'Unknown error occurred' }}
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <!-- Schema Explorer Tab -->
      <div v-if="activeTab === 'schema-explorer'" class="tab-pane">
        <div class="schema-explorer-section">
          <h3><i class="fas fa-sitemap mr-2"></i>Database Schema Explorer</h3>
          <p>Explore database schemas, tables, and relationships across your connected systems.</p>
          
          <div v-if="connections.length === 0" class="alert alert-info">
            <i class="fas fa-info-circle mr-2"></i>
            Please add a database connection first to explore schemas.
          </div>
          
          <div v-else class="schema-interface">
            <!-- Connection Selector -->
            <div class="connection-selector mb-4">
              <label class="form-label">Select Database Connection:</label>
              <select v-model="selectedSchemaConnection" class="form-select">
                <option value="">Choose a connection...</option>
                <option v-for="connection in connections" :key="connection.id" :value="connection">
                  {{ connection.name }} ({{ connection.engine ? connection.engine.toUpperCase() : 'UNKNOWN' }})
                </option>
              </select>
            </div>
            
            <!-- Schema Discovery Interface -->
            <div v-if="selectedSchemaConnection" class="schema-discovery-section">
              <div class="discovery-controls mb-4">
                <button 
                  @click="discoverSchema(selectedSchemaConnection)"
                  class="btn btn-primary"
                  :disabled="discoveringSchema.includes(selectedSchemaConnection.id)"
                >
                  <i class="fas fa-search mr-2" v-if="!discoveringSchema.includes(selectedSchemaConnection.id)"></i>
                  <i class="fas fa-spinner fa-spin mr-2" v-else></i>
                  {{ discoveringSchema.includes(selectedSchemaConnection.id) ? 'Discovering...' : 'Discover Schema' }}
                </button>
                
                <button 
                  v-if="selectedSchemaConnection.discovered_schema"
                  @click="refreshSchema(selectedSchemaConnection)"
                  class="btn btn-outline-secondary ms-2"
                  :disabled="discoveringSchema.includes(selectedSchemaConnection.id)"
                >
                  <i class="fas fa-refresh mr-2"></i>
                  Refresh Schema
                </button>
              </div>
              
              <!-- Schema Display -->
              <div v-if="selectedSchemaConnection.discovered_schema" class="schema-display">
                <div class="schema-summary mb-3">
                  <div class="summary-card">
                    <div class="summary-value">{{ Object.keys(selectedSchemaConnection.discovered_schema.tables || {}).length }}</div>
                    <div class="summary-label">Tables</div>
                  </div>
                  <div class="summary-card">
                    <div class="summary-value">{{ Object.values(selectedSchemaConnection.discovered_schema.tables || {}).reduce((sum, table) => sum + (table.columns?.length || 0), 0) }}</div>
                    <div class="summary-label">Total Columns</div>
                  </div>
                </div>
                
                <!-- Tables List -->
                <div class="tables-grid">
                  <div 
                    v-for="(table, tableName) in selectedSchemaConnection.discovered_schema.tables" 
                    :key="tableName"
                    class="table-card"
                  >
                    <div class="table-header">
                      <h6 class="table-name">{{ tableName }}</h6>
                      <span class="column-count">{{ table.columns?.length || 0 }} columns</span>
                    </div>
                    
                    <div class="table-columns">
                      <div v-for="column in (table.columns || []).slice(0, 5)" :key="column.name" class="column-item">
                        <span class="column-name">{{ column.name }}</span>
                        <span class="column-type">{{ column.type }}</span>
                      </div>
                      <div v-if="(table.columns?.length || 0) > 5" class="more-columns">
                        ... and {{ (table.columns?.length || 0) - 5 }} more columns
                      </div>
                    </div>
                    
                    <div class="table-actions">
                      <button 
                        @click="previewTable(selectedSchemaConnection, tableName)"
                        class="btn btn-sm btn-outline-primary"
                      >
                        <i class="fas fa-eye mr-1"></i>Preview
                      </button>
                      <button 
                        @click="queryTable(selectedSchemaConnection, tableName)"
                        class="btn btn-sm btn-outline-success"
                      >
                        <i class="fas fa-code mr-1"></i>Query
                      </button>
                    </div>
                  </div>
                </div>
              </div>
              
              <div v-else-if="!discoveringSchema.includes(selectedSchemaConnection.id)" class="no-schema">
                <div class="empty-state">
                  <i class="fas fa-sitemap fa-2x text-muted mb-3"></i>
                  <h5>No Schema Discovered</h5>
                  <p>Click "Discover Schema" to analyze the database structure.</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    </div> <!-- End Tab Content -->

    <!-- Query Modal -->
    <div class="modal fade" id="queryModal" tabindex="-1" v-if="showQueryModal">
      <div class="modal-dialog modal-xl">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">
              <i class="fas fa-play mr-2"></i>
              Query Database: {{ selectedConnection?.name }}
            </h5>
            <button 
              type="button" 
              class="btn-close" 
              @click="showQueryModal = false"
            ></button>
          </div>
          <div class="modal-body">
            <!-- Section 1: AI Query Builder -->
            <div class="query-section ai-query-section">
              <div class="section-header">
                <h5 class="section-title">
                  <i class="fas fa-magic mr-2"></i>AI Query Builder
                </h5>
                <p class="section-description">Use natural language to generate SQL queries automatically</p>
              </div>
              
              <!-- Natural Language Input -->
              <div class="row mb-3">
                <div class="col-md-8">
                  <label class="form-label">Describe what you want to find:</label>
                  <input 
                    v-model="aiQueryForm.intent" 
                    type="text" 
                    class="form-control" 
                    placeholder="e.g., Show me all active vendors with their contact information"
                    @keyup.enter="generateAIQuery"
                  />
                </div>
                <div class="col-md-4">
                  <label class="form-label">&nbsp;</label>
                  <button 
                    class="btn btn-primary w-100"
                    @click="generateAIQuery"
                    :disabled="generatingAIQuery || !aiQueryForm.intent.trim()"
                  >
                    <i class="fas fa-magic mr-2" v-if="!generatingAIQuery"></i>
                    <i class="fas fa-spinner fa-spin mr-2" v-else></i>
                    Generate SQL
                  </button>
                </div>
              </div>
              
              <!-- Quick Action Buttons -->
              <div class="quick-actions">
                <label class="form-label">Quick Actions:</label>
                <div class="btn-group flex-wrap" role="group">
                  <button 
                    v-for="action in quickActions" 
                    :key="action.type"
                    class="btn btn-sm btn-outline-secondary"
                    @click="generateQuickAction(action.type)"
                    :disabled="generatingAIQuery"
                  >
                    <i :class="action.icon + ' mr-1'"></i>{{ action.label }}
                  </button>
                </div>
              </div>
            </div>

            <!-- Section 2: Business Intelligence Suggestions -->
            <div class="query-section business-intelligence-section" v-if="businessSuggestions.length > 0">
              <div class="section-header">
                <h5 class="section-title">
                  <i class="fas fa-chart-line mr-2"></i>Business Intelligence Suggestions
                </h5>
                <p class="section-description">Pre-built queries for common business insights</p>
              </div>
              
              <div class="suggestions-list">
                <div 
                  v-for="(suggestion, index) in businessSuggestions" 
                  :key="index"
                  class="suggestion-item"
                  @click="useSuggestion(suggestion)"
                >
                  <div class="suggestion-header">
                    <strong>{{ suggestion.title }}</strong>
                    <span class="badge bg-info">{{ suggestion.category }}</span>
                  </div>
                  <div class="suggestion-description">{{ suggestion.description }}</div>
                  <div class="suggestion-value">
                    <small class="text-muted">{{ suggestion.business_value }}</small>
                  </div>
                </div>
              </div>
            </div>

            <!-- Query Results -->
            <div v-if="queryResults" class="query-results mt-4">
              <div class="results-header d-flex justify-content-between align-items-center mb-3">
                <h6 class="mb-0">
                  <i class="fas fa-table mr-2"></i>Query Results
                </h6>
                <div class="results-actions" v-if="queryResults.success">
                  <button 
                    class="btn btn-sm btn-outline-primary"
                    @click="exportResults('csv')"
                  >
                    <i class="fas fa-download mr-1"></i>Export CSV
                  </button>
                  <button 
                    class="btn btn-sm btn-outline-secondary"
                    @click="exportResults('json')"
                  >
                    <i class="fas fa-download mr-1"></i>Export JSON
                  </button>
                </div>
              </div>
              
              <div v-if="queryResults.success" class="results-container">
                <div class="results-info mb-3">
                  <div class="kpi-container">
                    <div class="kpi-item">
                      <span class="kpi-value">{{ queryResults.row_count }}</span>
                      <span class="kpi-label">Rows</span>
                    </div>
                    <div class="kpi-divider"></div>
                    <div class="kpi-item">
                      <span class="kpi-value">{{ queryResults.columns?.length || 0 }}</span>
                      <span class="kpi-label">Columns</span>
                    </div>
                    <div class="kpi-divider"></div>
                    <div class="kpi-item">
                      <span class="kpi-value">{{ queryResults.execution_time_ms }}ms</span>
                      <span class="kpi-label">Time</span>
                    </div>
                    <div class="kpi-divider"></div>
                    <div class="kpi-item">
                      <span class="kpi-value">{{ queryResults.query_id || 'N/A' }}</span>
                      <span class="kpi-label">Query ID</span>
                    </div>
                  </div>
                </div>
                
                <!-- Data Table with Horizontal Scroll -->
                <div class="table-container">
                  <div class="table-scroll-wrapper">
                    <table class="table table-sm table-hover data-table">
                      <thead>
                        <tr>
                          <th class="row-number sticky-column">#</th>
                          <th v-for="column in queryResults.columns" :key="column" class="sortable-header">
                            <div class="header-content">
                              <span class="column-name">{{ column }}</span>
                              <i class="fas fa-sort text-muted ml-1"></i>
                            </div>
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="(row, index) in queryResults.data" :key="index">
                          <td class="row-number sticky-column">{{ index + 1 }}</td>
                          <td 
                            v-for="column in queryResults.columns" 
                            :key="column"
                            class="data-cell"
                            :title="formatCellValue(row[column])"
                          >
                            <div class="cell-content">{{ formatCellValue(row[column]) }}</div>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                  
                  <!-- Scroll Indicator -->
                  <div class="scroll-indicator" v-if="queryResults.columns?.length > 8">
                    <i class="fas fa-arrows-alt-h mr-1"></i>
                    <small class="text-muted">Scroll horizontally to view all {{ queryResults.columns.length }} columns</small>
                  </div>
                </div>
                
                <!-- Pagination (if needed for large results) -->
                <div class="results-pagination mt-3" v-if="queryResults.row_count >= 1000">
                  <div class="alert alert-info">
                    <i class="fas fa-info-circle mr-2"></i>
                    Results limited to 1000 rows for performance. Use LIMIT clause for specific row ranges.
                  </div>
                </div>
              </div>
              
              <div v-else class="alert alert-danger">
                <div class="error-header">
                  <strong><i class="fas fa-exclamation-triangle mr-2"></i>Query Failed</strong>
                </div>
                <div class="error-details mt-2">
                  <p class="mb-1">{{ queryResults.error }}</p>
                  <small class="text-muted" v-if="queryResults.error_type">
                    Error Type: {{ queryResults.error_type }}
                  </small>
                </div>
              </div>
            </div>
          </div>
          <div class="modal-footer">
            <button 
              type="button" 
              class="btn btn-secondary" 
              @click="showQueryModal = false"
            >
              Close
            </button>
            <button 
              type="button" 
              class="btn btn-primary" 
              @click="executeQuery"
              :disabled="executingQuery || !queryForm.query.trim()"
            >
              <i class="fas fa-spinner fa-spin mr-2" v-if="executingQuery"></i>
              <i class="fas fa-play mr-2" v-else></i>
              Execute Query
            </button>
          </div>
        </div>
      </div>
    </div> <!-- End Query Modal -->
  </div> <!-- End database-connections-container -->
</template>

<script>
import { ref, reactive, computed, onMounted, watch } from 'vue'
import { useToast } from 'vue-toastification'
import api from '@/services/api'

export default {
  name: 'DatabaseConnections',
  setup() {
    const toast = useToast()
    
    // Reactive data
    const connections = ref([])
    const erpTemplates = ref([])
    const showCreateModal = ref(false)
    const showEditModal = ref(false)
    const showQueryModal = ref(false)
    const showSchemaSuggestionsModal = ref(false)
    
    // Tab management
    const activeTab = ref('connections')
    const tabs = ref([
      { id: 'connections', name: 'Connections', icon: 'fas fa-database' },
      { id: 'knowledge-base', name: 'Knowledge Base', icon: 'fas fa-brain' },
      { id: 'query-builder', name: 'Query Builder', icon: 'fas fa-code' },
      { id: 'duplicate-analysis', name: 'Duplicate Analysis', icon: 'fas fa-search-plus' },
      { id: 'schema-explorer', name: 'Schema Explorer', icon: 'fas fa-sitemap' }
    ])
    const selectedConnection = ref(null)
    const selectedSchemaConnection = ref(null)
    const schemaAnalysis = ref(null)
    const testingConnections = ref([])
    const discoveringSchema = ref([])
    const creatingConnection = ref(false)
    const executingQuery = ref(false)
    const showPassword = ref(false)
    const generatingAIQuery = ref(false)
    const businessSuggestions = ref([])
    const lastAIGeneration = ref(null)
    const analyzingDuplicates = ref(false)
    const duplicateResults = ref(null)
    const openDropdowns = ref([])
    const availableDocuments = ref([])
    const selectedDocumentId = ref(null)
    const processingKnowledge = ref(false)
    const uploadingDocument = ref(false)
    const activeKnowledgeConnection = ref(null)
    const fileInput = ref(null)
    const showSchemaRules = ref(false)
    const showTableDetails = ref(false)
    const tableSearchQuery = ref('')
    const expandedTables = ref([])
    
    const newConnection = reactive({
      name: '',
      description: '',
      engine: '',
      host: '',
      port: null,
      database_name: '',
      username: '',
      password: '',
      use_ssl: true,
      erp_template: null
    })
    
    const editingConnection = reactive({
      id: null,
      name: '',
      description: '',
      engine: '',
      host: '',
      port: null,
      database_name: '',
      username: '',
      password: '',  // Will be empty - only update if user enters new password
      use_ssl: true,
      erp_template: null
    })
    
    const updatingConnection = ref(false)
    
    const queryForm = reactive({
      query: '',
      purpose: 'manual_query'
    })
    
    const aiQueryForm = reactive({
      intent: '',
      selectedTables: []
    })
    
    const duplicateForm = reactive({
      houseNumber: '',
      analysisFocus: 'general'
    })
    
    const quickActions = [
      { type: 'explore', label: 'Explore Data', icon: 'fas fa-search' },
      { type: 'count', label: 'Count Records', icon: 'fas fa-calculator' },
      { type: 'summary', label: 'Summarize', icon: 'fas fa-chart-bar' },
      { type: 'relationships', label: 'Show Joins', icon: 'fas fa-project-diagram' },
      { type: 'recent', label: 'Recent Data', icon: 'fas fa-clock' }
    ]
    
    const queryResults = ref(null)
    
    // Computed property for filtered tables
    const filteredTables = computed(() => {
      if (!activeKnowledgeConnection.value?.logical_schema_context?.tables) return {}
      
      const tables = activeKnowledgeConnection.value.logical_schema_context.tables
      if (!tableSearchQuery.value) return tables
      
      const query = tableSearchQuery.value.toLowerCase()
      const filtered = {}
      
      for (const [tableName, tableData] of Object.entries(tables)) {
        if (
          tableName.toLowerCase().includes(query) ||
          (tableData.alias && tableData.alias.toLowerCase().includes(query)) ||
          (tableData.description && tableData.description.toLowerCase().includes(query))
        ) {
          filtered[tableName] = tableData
        }
      }
      
      return filtered
    })
    
    // Toggle table expansion
    const toggleTableExpand = (tableName) => {
      const index = expandedTables.value.indexOf(tableName)
      if (index > -1) {
        expandedTables.value.splice(index, 1)
      } else {
        expandedTables.value.push(tableName)
      }
    }
    
    // Computed properties
    const getDefaultPort = (engine) => {
      const ports = {
        mysql: 3306,
        postgresql: 5432,
        mssql: 1433,
        oracle: 1521
      }
      return ports[engine] || ''
    }
    
    // Methods
    const handleFileUpload = async (event) => {
      console.log('🚀 handleFileUpload triggered', event)
      const file = event.target.files[0]
      if (!file || !activeKnowledgeConnection.value) {
        console.warn('⚠️ No file or active connection', { file, conn: activeKnowledgeConnection.value })
        return
      }

      console.log('📄 File selected:', file.name)
      uploadingDocument.value = true
      const formData = new FormData()
      formData.append('file', file)

      try {
        const response = await api.post(`/database-connections/${activeKnowledgeConnection.value.id}/upload-reference/`, formData)
        
        if (response.data.success) {
          // Check extraction results
          const { extraction_success, tables_extracted, extraction_info, message } = response.data
          
          if (extraction_success && tables_extracted > 0) {
            // Full success - show detailed message
            const pagesInfo = extraction_info?.total_pages 
              ? ` from ${extraction_info.pages_processed}/${extraction_info.total_pages} pages`
              : ''
            toast.success(`✅ Extracted ${tables_extracted} tables${pagesInfo}`)
            console.log('📊 Extraction info:', extraction_info)
          } else if (extraction_success) {
            // Partial success
            toast.info(`Document uploaded. ${message}`)
          } else {
            // Upload succeeded but extraction failed
            toast.warning(`Document saved but extraction failed: ${message}`)
            console.warn('⚠️ Extraction failed:', message)
          }
          
          await loadConnections()
        } else {
          toast.error(response.data.error || 'Failed to process document')
        }
      } catch (error) {
        toast.error('Failed to upload document')
        console.error('Upload error:', error)
      } finally {
        uploadingDocument.value = false
        // Clear input
        event.target.value = ''
      }
    }

    const loadConnections = async () => {
      try {
        const response = await api.get('/database-connections/')
        connections.value = response.data
        
        // If we have an active knowledge connection, update it with fresh data
        if (activeKnowledgeConnection.value) {
          const updated = response.data.find(c => c.id === activeKnowledgeConnection.value.id)
          if (updated) activeKnowledgeConnection.value = updated
        }
      } catch (error) {
        toast.error('Failed to load database connections')
        console.error('Load connections error:', error)
      }
    }

    // availableDocuments is no longer used for selection, but we can keep it if needed elsewhere
    // For now, we'll simplify and remove loadAvailableDocuments calls
    const loadAvailableDocuments = async () => {
      return // No longer picking from global documents for security
    }

    const addReferenceDocument = async () => {
      if (!activeKnowledgeConnection.value || !selectedDocumentId.value) {
        toast.error('Please select a connection and a document')
        return
      }

      processingKnowledge.value = true
      try {
        const response = await api.post(`/database-connections/${activeKnowledgeConnection.value.id}/add_reference_document/`, {
          document_id: selectedDocumentId.value
        })

        if (response.data.success) {
          toast.success('Document processed and schema knowledge updated')
          selectedDocumentId.value = null
          await loadConnections()
        } else {
          toast.error(response.data.error || 'Failed to process document')
        }
      } catch (error) {
        toast.error('An error occurred while processing the document')
        console.error('Add reference document error:', error)
      } finally {
        processingKnowledge.value = false
      }
    }

    const clearLogicalSchema = async () => {
      if (!activeKnowledgeConnection.value) return
      
      if (!confirm('Are you sure you want to clear all learned schema knowledge and associated reference documents?')) {
        return
      }

      try {
        const response = await api.post(`/database-connections/${activeKnowledgeConnection.value.id}/clear_logical_schema/`)
        if (response.data.success) {
          toast.success('Logical schema knowledge cleared')
          await loadConnections()
        }
      } catch (error) {
        toast.error('Failed to clear knowledge')
        console.error('Clear knowledge error:', error)
      }
    }
    
    const loadERPTemplates = async () => {
      try {
        const response = await api.get('/erp-templates/')
        erpTemplates.value = response.data
      } catch (error) {
        toast.error('Failed to load ERP templates')
        console.error('Load ERP templates error:', error)
      }
    }
    
    const createConnection = async () => {
      if (!newConnection.name || !newConnection.engine || !newConnection.host || 
          !newConnection.database_name || !newConnection.username || !newConnection.password) {
        toast.error('Please fill in all required fields')
        return
      }
      
      creatingConnection.value = true
      
      try {
        // Set default port if not provided
        if (!newConnection.port) {
          newConnection.port = getDefaultPort(newConnection.engine)
        }
        
        const response = await api.post('/database-connections/', newConnection)
        
        // Ensure the connection has a default status to prevent undefined errors
        const connectionData = {
          ...response.data,
          status: response.data.status || 'PENDING'
        }
        
        connections.value.push(connectionData)
        showCreateModal.value = false
        resetNewConnection()
        
        toast.success('Database connection created successfully')
        
        // Auto-test the connection
        testConnection(connectionData)
        
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Failed to create database connection')
        console.error('Create connection error:', error)
      } finally {
        creatingConnection.value = false
      }
    }
    
    const testConnection = async (connection) => {
      testingConnections.value.push(connection.id)
      
      try {
        const response = await api.post(`/database-connections/${connection.id}/test_connection/`)
        
        if (response.data.success) {
          toast.success(`Connection to ${connection.name} successful!`)
          connection.status = 'ACTIVE'
        } else {
          // Show detailed error information instead of generic message
          const errorDetails = response.data.error || response.data.message || 'Unknown error'
          const shortError = errorDetails.length > 100 ? 
            errorDetails.substring(0, 100) + '...' : 
            errorDetails
          
          toast.error(`Connection to ${connection.name} failed: ${shortError}`)
          connection.status = 'ERROR'
          
          // Log full error details for debugging
          console.error('Database connection test failed:', {
            connection: connection.name,
            error: response.data.error,
            errorType: response.data.error_type,
            message: response.data.message,
            testedAt: response.data.tested_at
          })
        }
        
        connection.last_test_at = new Date().toISOString()
        connection.last_test_result = JSON.stringify(response.data)
        
      } catch (error) {
        // Show detailed error from API response if available
        const errorMessage = error.response?.data?.error || 
                           error.response?.data?.message || 
                           error.message || 
                           'Unknown connection error'
        
        const shortError = errorMessage.length > 100 ? 
          errorMessage.substring(0, 100) + '...' : 
          errorMessage
          
        toast.error(`Failed to test connection to ${connection.name}: ${shortError}`)
        connection.status = 'ERROR'
        console.error('Test connection error:', error)
      } finally {
        testingConnections.value = testingConnections.value.filter(id => id !== connection.id)
      }
    }
    
    const discoverSchema = async (connection) => {
      discoveringSchema.value.push(connection.id)
      
      try {
        const response = await api.post(`/database-connections/${connection.id}/discover_schema/`)
        
        if (response.data.success) {
          toast.success(`Schema discovered for ${connection.name}`)
          connection.discovered_schema = response.data.schema
          
          // Show suggestion modal if patterns found
          if (response.data.schema.analysis?.suggested_entities) {
            showSchemaSuggestions(connection, response.data.schema.analysis)
          }
        } else {
          toast.error(`Schema discovery failed for ${connection.name}`)
        }
        
      } catch (error) {
        toast.error(`Failed to discover schema for ${connection.name}`)
        console.error('Schema discovery error:', error)
      } finally {
        discoveringSchema.value = discoveringSchema.value.filter(id => id !== connection.id)
      }
    }
    
    const openQueryModal = (connection) => {
      selectedConnection.value = connection
      queryForm.query = ''
      queryForm.purpose = 'manual_query'
      queryResults.value = null
      lastAIGeneration.value = null
      aiQueryForm.intent = ''
      aiQueryForm.selectedTables = []
      showQueryModal.value = true
      
      // Load business suggestions if schema is available
      if (connection.discovered_schema) {
        loadBusinessSuggestions(connection)
      }
    }
    
    const executeQuery = async () => {
      if (!queryForm.query.trim()) {
        toast.error('Please enter a SQL query')
        return
      }
      
      executingQuery.value = true
      queryResults.value = null
      
      try {
        const response = await api.post(
          `/database-connections/${selectedConnection.value.id}/execute_query/`,
          queryForm
        )
        
        queryResults.value = response.data
        
        if (response.data.success) {
          toast.success(`Query executed successfully - ${response.data.row_count} rows returned`)
        } else {
          toast.error('Query execution failed')
        }
        
      } catch (error) {
        toast.error('Failed to execute query')
        console.error('Query execution error:', error)
        queryResults.value = {
          success: false,
          error: error.response?.data?.detail || 'Query execution failed'
        }
      } finally {
        executingQuery.value = false
      }
    }
    
    const resetNewConnection = () => {
      Object.assign(newConnection, {
        name: '',
        description: '',
        engine: '',
        host: '',
        port: null,
        database_name: '',
        username: '',
        password: '',
        use_ssl: true,
        erp_template: null
      })
    }
    
    const getStatusText = (status) => {
      const statusMap = {
        'ACTIVE': 'Active',
        'PENDING': 'Pending',
        'ERROR': 'Error',
        'INACTIVE': 'Inactive',
        'TESTING': 'Testing'
      }
      return statusMap[status] || status
    }

    const connectionCardClass = (connection) => {
      const status = (connection?.status || '').toUpperCase()
      if (status === 'ERROR') return 'entity-card--error'
      if (status === 'PENDING' || status === 'TESTING') return 'entity-card--warn'
      if (status === 'INACTIVE') return 'entity-card--archived'
      return ''
    }
    
    const formatDate = (dateString) => {
      return new Date(dateString).toLocaleDateString()
    }
    
    const formatCellValue = (value) => {
      if (value === null || value === undefined) return 'NULL'
      if (typeof value === 'object') return JSON.stringify(value)
      return value.toString()
    }
    
    const editConnection = (connection) => {
      // Populate the editing form with the connection data
      Object.assign(editingConnection, {
        id: connection.id,
        name: connection.name || '',
        description: connection.description || '',
        engine: connection.engine || '',
        host: connection.host || '',
        port: connection.port || null,
        database_name: connection.database_name || '',
        username: connection.username || '',
        password: '',  // Always start with empty - user must enter new password if they want to change it
        use_ssl: connection.use_ssl !== false,  // Default to true if not set
        erp_template: connection.erp_template || null
      })
      showEditModal.value = true
    }
    
    const updateConnection = async () => {
      if (!editingConnection.name || !editingConnection.engine || !editingConnection.host || 
          !editingConnection.database_name || !editingConnection.username) {
        toast.error('Please fill in all required fields')
        return
      }
      
      updatingConnection.value = true
      
      try {
        // Set default port if not provided
        if (!editingConnection.port) {
          editingConnection.port = getDefaultPort(editingConnection.engine)
        }
        
        // Build the update payload - only include password if it was changed
        const updatePayload = {
          name: editingConnection.name,
          description: editingConnection.description,
          engine: editingConnection.engine,
          host: editingConnection.host,
          port: editingConnection.port,
          database_name: editingConnection.database_name,
          username: editingConnection.username,
          use_ssl: editingConnection.use_ssl,
          erp_template: editingConnection.erp_template
        }
        
        // Only include password if user entered a new one
        if (editingConnection.password && editingConnection.password.trim()) {
          updatePayload.password = editingConnection.password
        }
        
        const response = await api.patch(`/database-connections/${editingConnection.id}/`, updatePayload)
        
        // Update the connection in the local list
        const index = connections.value.findIndex(c => c.id === editingConnection.id)
        if (index !== -1) {
          connections.value[index] = {
            ...connections.value[index],
            ...response.data
          }
        }
        
        showEditModal.value = false
        resetEditConnection()
        
        toast.success('Database connection updated successfully')
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Failed to update database connection')
        console.error('Update connection error:', error)
      } finally {
        updatingConnection.value = false
      }
    }
    
    const resetEditConnection = () => {
      Object.assign(editingConnection, {
        id: null,
        name: '',
        description: '',
        engine: '',
        host: '',
        port: null,
        database_name: '',
        username: '',
        password: '',
        use_ssl: true,
        erp_template: null
      })
    }
    
    // eslint-disable-next-line no-unused-vars
    const viewMappings = (connection) => {
      // TODO: Navigate to entity mappings view
      toast.info('Entity mappings view coming soon')
    }
    
    // eslint-disable-next-line no-unused-vars
    const viewHistory = (connection) => {
      // TODO: Navigate to query history view
      toast.info('Query history view coming soon')
    }
    
    const toggleDropdown = (connectionId) => {
      if (openDropdowns.value.includes(connectionId)) {
        openDropdowns.value = openDropdowns.value.filter(id => id !== connectionId)
      } else {
        openDropdowns.value = [...openDropdowns.value, connectionId]
      }
    }
    
    const closeDropdown = (connectionId) => {
      openDropdowns.value = openDropdowns.value.filter(id => id !== connectionId)
    }
    
    const deleteConnection = async (connection) => {
      if (!confirm(`Are you sure you want to delete the connection "${connection.name}"?`)) {
        return
      }
      
      try {
        await api.delete(`/database-connections/${connection.id}/`)
        connections.value = connections.value.filter(c => c.id !== connection.id)
        toast.success('Database connection deleted successfully')
      } catch (error) {
        toast.error('Failed to delete database connection')
        console.error('Delete connection error:', error)
      }
    }
    
    const showSchemaSuggestions = (connection, analysis = null) => {
      selectedSchemaConnection.value = connection
      
      // If schema hasn't been discovered yet, discover it first
      if (!connection.discovered_schema || Object.keys(connection.discovered_schema).length === 0) {
        console.log('No existing schema found, discovering schema first...')
        discoverSchema(connection).then(() => {
          schemaAnalysis.value = connection.discovered_schema || {}
          showSchemaSuggestionsModal.value = true
          if (analysis) {
            toast.info(`Found ${Object.keys(analysis.suggested_entities).length} potential entity mappings`)
          }
        })
      } else {
        console.log('Using existing discovered schema:', Object.keys(connection.discovered_schema))
        schemaAnalysis.value = connection.discovered_schema
        showSchemaSuggestionsModal.value = true
        if (analysis) {
          toast.info(`Found ${Object.keys(analysis.suggested_entities).length} potential entity mappings`)
        } else {
          toast.info('Loaded previously discovered schema')
        }
      }
    }
    
    const getTableColumns = (tableName) => {
      return schemaAnalysis.value?.tables?.[tableName]?.columns || []
    }
    
    const createEntityMapping = async (tableName, entityType) => {
      // TODO: Implement entity mapping creation API call
      toast.info(`Creating ${entityType} mapping for table ${tableName}...`)
      console.log('Creating mapping:', { tableName, entityType, connection: selectedSchemaConnection.value })
    }
    
    const previewTable = (tableName) => {
      // Generate a preview query
      const previewQuery = `SELECT * FROM ${tableName} LIMIT 10`
      queryForm.query = previewQuery
      queryForm.purpose = 'data_exploration'
      selectedConnection.value = selectedSchemaConnection.value
      showSchemaSuggestionsModal.value = false
      showQueryModal.value = true
    }
    
    const queryTable = (tableName) => {
      // Generate a basic query template
      const basicQuery = `SELECT * FROM ${tableName} WHERE `
      queryForm.query = basicQuery
      queryForm.purpose = 'manual_query'
      selectedConnection.value = selectedSchemaConnection.value
      showSchemaSuggestionsModal.value = false
      showQueryModal.value = true
    }
    
    const refreshSchema = async () => {
      if (selectedSchemaConnection.value) {
        await discoverSchema(selectedSchemaConnection.value)
        schemaAnalysis.value = selectedSchemaConnection.value.discovered_schema
      }
    }
    
    const exportResults = (format) => {
      if (!queryResults.value || !queryResults.value.success) {
        toast.error('No query results to export')
        return
      }
      
      const data = queryResults.value.data
      const columns = queryResults.value.columns
      
      if (format === 'csv') {
        exportToCSV(data, columns)
      } else if (format === 'json') {
        exportToJSON(data)
      }
    }
    
    const exportToCSV = (data, columns) => {
      try {
        // Create CSV header
        const csvHeaders = columns.join(',')
        
        // Create CSV rows
        const csvRows = data.map(row => 
          columns.map(col => {
            const value = row[col]
            // Escape quotes and wrap in quotes if contains comma, quote, or newline
            if (value === null || value === undefined) return ''
            const stringValue = String(value)
            if (stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n')) {
              return '"' + stringValue.replace(/"/g, '""') + '"'
            }
            return stringValue
          }).join(',')
        )
        
        const csvContent = [csvHeaders, ...csvRows].join('\n')
        
        // Create and download file
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
        const link = document.createElement('a')
        link.href = URL.createObjectURL(blob)
        link.download = `query_results_${new Date().toISOString().slice(0, 19).replace(/:/g, '-')}.csv`
        link.click()
        
        toast.success('CSV export completed')
      } catch (error) {
        console.error('CSV export error:', error)
        toast.error('Failed to export CSV')
      }
    }
    
    const exportToJSON = (data) => {
      try {
        const jsonContent = JSON.stringify({
          query_results: data,
          exported_at: new Date().toISOString(),
          row_count: data.length,
          connection: selectedConnection.value?.name
        }, null, 2)
        
        const blob = new Blob([jsonContent], { type: 'application/json;charset=utf-8;' })
        const link = document.createElement('a')
        link.href = URL.createObjectURL(blob)
        link.download = `query_results_${new Date().toISOString().slice(0, 19).replace(/:/g, '-')}.json`
        link.click()
        
        toast.success('JSON export completed')
      } catch (error) {
        console.error('JSON export error:', error)
        toast.error('Failed to export JSON')
      }
    }
    
    // AI Query Generation Methods
    const generateAIQuery = async (tableName = null, entityType = null) => {
      console.log('generateAIQuery called:', { tableName, entityType, selectedConnection: selectedConnection.value?.id, intent: aiQueryForm.intent })
      
      if (!selectedConnection.value) {
        toast.error('Please select a database connection first')
        console.error('No selectedConnection:', selectedConnection.value)
        return
      }
      
      if (!aiQueryForm.intent.trim()) {
        toast.error('Please enter what you want to find')
        console.error('No intent provided:', aiQueryForm.intent)
        return
      }
      
      console.log('Starting AI query generation...')
      generatingAIQuery.value = true
      
      try {
        const payload = {
          intent: aiQueryForm.intent,
          selected_tables: tableName ? [tableName] : aiQueryForm.selectedTables
        }
        
        // Add entity type context if provided
        if (entityType) {
          payload.intent += ` (focusing on ${entityType} data)`
        }
        
        const response = await api.post(`/database-connections/${selectedConnection.value.id}/generate_sql/`, payload)
        
        if (response.data.success) {
          queryForm.query = response.data.sql_query
          queryForm.purpose = response.data.suggested_purpose || 'ai_generated'
          lastAIGeneration.value = {
            provider: response.data.provider_used,
            confidence: response.data.confidence,
            explanation: response.data.explanation,
            tables_used: response.data.tables_used
          }
          
          toast.success(`AI generated SQL query using ${response.data.provider_used}`)
          
          // Show explanation if available
          if (response.data.explanation) {
            console.log('AI Query Explanation:', response.data.explanation)
          }
        } else {
          toast.error('Failed to generate SQL query')
          console.error('AI SQL generation failed:', response.data)
        }
        
      } catch (error) {
        toast.error('Failed to generate AI query')
        console.error('AI query generation error:', error)
      } finally {
        generatingAIQuery.value = false
      }
    }
    
    const generateTableAIQuery = async (tableName) => {
      if (!selectedSchemaConnection.value) return
      
      generatingAIQuery.value = true
      
      try {
        const response = await api.post(`/database-connections/${selectedSchemaConnection.value.id}/generate_table_query/`, {
          tables: [tableName],
          query_type: 'explore'
        })
        
        if (response.data.success) {
          // Set up query modal with AI-generated query
          selectedConnection.value = selectedSchemaConnection.value
          queryForm.query = response.data.sql_query
          queryForm.purpose = response.data.suggested_purpose || 'ai_generated'
          lastAIGeneration.value = {
            provider: response.data.provider_used,
            confidence: response.data.confidence,
            explanation: response.data.explanation,
            tables_used: response.data.tables_used
          }
          
          showSchemaSuggestionsModal.value = false
          showQueryModal.value = true
          
          toast.success(`AI generated query for ${tableName}`)
        } else {
          toast.error('Failed to generate AI query')
        }
        
      } catch (error) {
        toast.error('Failed to generate AI query')
        console.error('AI table query error:', error)
      } finally {
        generatingAIQuery.value = false
      }
    }
    
    const generateQuickAction = async (actionType) => {
      if (!selectedConnection.value) return
      
      const selectedTables = aiQueryForm.selectedTables.length > 0 
        ? aiQueryForm.selectedTables 
        : Object.keys(selectedConnection.value.discovered_schema?.tables || {}).slice(0, 3)
      
      if (selectedTables.length === 0) {
        toast.error('No tables available for query generation')
        return
      }
      
      generatingAIQuery.value = true
      
      try {
        const response = await api.post(`/database-connections/${selectedConnection.value.id}/generate_table_query/`, {
          tables: selectedTables,
          query_type: actionType
        })
        
        if (response.data.success) {
          queryForm.query = response.data.sql_query
          queryForm.purpose = response.data.suggested_purpose || 'ai_generated'
          lastAIGeneration.value = {
            provider: response.data.provider_used,
            confidence: response.data.confidence,
            explanation: response.data.explanation,
            tables_used: response.data.tables_used
          }
          
          toast.success(`Generated ${actionType} query`)
        } else {
          toast.error('Failed to generate query')
        }
        
      } catch (error) {
        toast.error('Failed to generate query')
        console.error('Quick action error:', error)
      } finally {
        generatingAIQuery.value = false
      }
    }
    
    const loadBusinessSuggestions = async (connection) => {
      try {
        const response = await api.get(`/database-connections/${connection.id}/suggest_business_queries/`)
        
        if (response.data.success) {
          businessSuggestions.value = response.data.suggestions
        }
      } catch (error) {
        console.error('Failed to load business suggestions:', error)
        businessSuggestions.value = []
      }
    }
    
    const useSuggestion = (suggestion) => {
      queryForm.query = suggestion.sql_query
      queryForm.purpose = 'business_intelligence'
      lastAIGeneration.value = {
        provider: 'suggestion',
        confidence: 0.9,
        explanation: suggestion.description,
        tables_used: []
      }
      
      toast.success(`Applied suggestion: ${suggestion.title}`)
    }
    
    const analyzeDuplicates = async () => {
      if (!selectedConnection.value || !duplicateForm.houseNumber) return
      
      analyzingDuplicates.value = true
      duplicateResults.value = null
      
      try {
        toast.info(`Analyzing duplicates for house ${duplicateForm.houseNumber}...`)
        
        const response = await api.post(
          `/database-connections/${selectedConnection.value.id}/analyze_house_duplicates/`,
          {
            house_number: duplicateForm.houseNumber,
            analysis_focus: duplicateForm.analysisFocus
          }
        )
        
        if (response.data.success) {
          duplicateResults.value = response.data
          
          // Initialize user validation states for all duplicates
          initializeValidationStates(response.data.structured_results)
          
          toast.success(`Analysis complete! Found ${response.data.structured_results?.duplicates_found || 0} potential duplicates`)
        } else {
          toast.error(`Analysis failed: ${response.data.error}`)
        }
        
      } catch (error) {
        console.error('Duplicate analysis error:', error)
        toast.error(`Analysis failed: ${error.response?.data?.error || error.message}`)
      } finally {
        analyzingDuplicates.value = false
      }
    }
    
    const initializeValidationStates = (results) => {
      if (!results) return
      
      // Initialize validation states for all duplicates
      const allDuplicates = [
        ...(results.high_confidence_duplicates || []),
        ...(results.medium_confidence_duplicates || [])
      ]
      
      allDuplicates.forEach(duplicate => {
        if (!duplicate.user_validation) {
          duplicate.user_validation = {
            status: 'pending',
            validated_by: null,
            validated_at: null,
            validation_notes: '',
            false_positive: false
          }
        }
      })
    }
    
    const validateDuplicate = (duplicateId, status) => {
      if (!duplicateResults.value?.structured_results) return
      
      // Find and update the duplicate
      const allDuplicates = [
        ...(duplicateResults.value.structured_results.high_confidence_duplicates || []),
        ...(duplicateResults.value.structured_results.medium_confidence_duplicates || [])
      ]
      
      const duplicate = allDuplicates.find(d => d.duplicate_id === duplicateId)
      if (duplicate) {
        duplicate.user_validation.status = status
        duplicate.user_validation.validated_at = new Date().toISOString()
        duplicate.user_validation.false_positive = (status === 'dismissed')
        
        toast.success(`Duplicate ${duplicateId} marked as ${status}`)
      }
    }
    
    const saveValidationNotes = (duplicateId) => {
      // Auto-save validation notes when user finishes typing
      console.log(`Saving validation notes for duplicate ${duplicateId}`)
      toast.info(`Validation notes saved for ${duplicateId}`)
    }
    
    const formatDuplicateType = (type) => {
      const typeMap = {
        'critical_duplicate': 'Critical Duplicate (Same Cost Code + Vendor)',
        'exact_duplicate': 'Exact Duplicate',
        'near_duplicate': 'Near Duplicate',
        'same_work_different_vendors': 'Same Work, Different Vendors',
        'amount_variation': 'Amount Variation',
        'timeline_overlap': 'Timeline Overlap',
        'cross_table_duplicate': 'Cross-Table Duplicate',
        'unknown': 'Unknown Type'
      }
      return typeMap[type] || type
    }
    
    const getHighConfidenceDuplicates = () => {
      if (!duplicateResults.value?.structured_results) return []
      
      // Try to get from structured_results first
      let duplicates = duplicateResults.value.structured_results.high_confidence_duplicates || []
      
      // If empty, try to parse from raw_analysis
      if (duplicates.length === 0 && duplicateResults.value.raw_analysis) {
        duplicates = parseRawAnalysisForDuplicates(duplicateResults.value.raw_analysis, 'high_confidence_duplicates')
      }
      
      return duplicates
    }
    
    const getMediumConfidenceDuplicates = () => {
      if (!duplicateResults.value?.structured_results) return []
      
      // Try to get from structured_results first
      let duplicates = duplicateResults.value.structured_results.medium_confidence_duplicates || []
      
      // If empty, try to parse from raw_analysis
      if (duplicates.length === 0 && duplicateResults.value.raw_analysis) {
        duplicates = parseRawAnalysisForDuplicates(duplicateResults.value.raw_analysis, 'medium_confidence_duplicates')
      }
      
      return duplicates
    }
    
    const parseRawAnalysisForDuplicates = (rawAnalysis, duplicateType) => {
      try {
        // Try to find JSON in the raw analysis
        const jsonStart = rawAnalysis.indexOf('{')
        const jsonEnd = rawAnalysis.lastIndexOf('}') + 1
        
        if (jsonStart !== -1 && jsonEnd > jsonStart) {
          const jsonString = rawAnalysis.substring(jsonStart, jsonEnd)
          const parsed = JSON.parse(jsonString)
          return parsed[duplicateType] || []
        }
        
        return []
      } catch (error) {
        console.error('Error parsing raw analysis for duplicates:', error)
        return []
      }
    }
    
    // Watch for tab changes to ensure fresh data
    watch(activeTab, async (newTab) => {
      if (newTab === 'knowledge-base') {
        // Reload connections to ensure we have the latest logical_schema_context
        await loadConnections()
        
        // Auto-select first connection with schema knowledge, or just first connection
        if (!activeKnowledgeConnection.value && connections.value.length > 0) {
          // Prefer connection with existing schema knowledge
          const connectionWithSchema = connections.value.find(
            c => c.logical_schema_context && 
                (c.logical_schema_context.tables || c.logical_schema_context.general_notes)
          )
          activeKnowledgeConnection.value = connectionWithSchema || connections.value[0]
        }
      }
    })
    
    // Lifecycle
    onMounted(() => {
      loadConnections()
      loadERPTemplates()
      loadAvailableDocuments()
    })
    
    return {
      // Data
      connections,
      erpTemplates,
      showCreateModal,
      showEditModal,
      showQueryModal,
      
      // Tab management
      activeTab,
      tabs,
      showSchemaSuggestionsModal,
      selectedConnection,
      selectedSchemaConnection,
      schemaAnalysis,
      testingConnections,
      discoveringSchema,
      creatingConnection,
      updatingConnection,
      executingQuery,
      showPassword,
      generatingAIQuery,
      businessSuggestions,
      lastAIGeneration,
      newConnection,
      editingConnection,
      queryForm,
      aiQueryForm,
      duplicateForm,
      quickActions,
      queryResults,
      analyzingDuplicates,
      duplicateResults,
      openDropdowns,
      
      // Methods
      getDefaultPort,
      createConnection,
      updateConnection,
      resetEditConnection,
      testConnection,
      discoverSchema,
      openQueryModal,
      executeQuery,
      resetNewConnection,
      getStatusText,
      connectionCardClass,
      formatDate,
      formatCellValue,
      editConnection,
      viewMappings,
      viewHistory,
      deleteConnection,
      toggleDropdown,
      closeDropdown,
      showSchemaSuggestions,
      getTableColumns,
      createEntityMapping,
      previewTable,
      queryTable,
      refreshSchema,
      exportResults,
      exportToCSV,
      exportToJSON,
      generateAIQuery,
      generateTableAIQuery,
      generateQuickAction,
      loadBusinessSuggestions,
      useSuggestion,
      analyzeDuplicates,
      validateDuplicate,
      saveValidationNotes,
      formatDuplicateType,
      getHighConfidenceDuplicates,
      getMediumConfidenceDuplicates,
      addReferenceDocument,
      handleFileUpload,
      clearLogicalSchema,
      availableDocuments,
      selectedDocumentId,
      processingKnowledge,
      uploadingDocument,
      activeKnowledgeConnection,
      fileInput,
      showSchemaRules,
      showTableDetails,
      tableSearchQuery,
      expandedTables,
      filteredTables,
      toggleTableExpand
    }
  }
}
</script>

<style scoped>
.database-connections-page {
  /* Uses global .entity-list-page chrome (spoke-entity-cards) */
}

/* Shared inputs / selects — replace browser-default ugliness */
.database-connections-page :deep(.form-select),
.database-connections-page :deep(select.form-control),
.database-connections-page .form-select {
  display: block;
  width: 100%;
  max-width: 100%;
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;
  padding: 0.55rem 2.25rem 0.55rem 0.75rem;
  border: 1px solid #cbd5e1;
  border-radius: 0.55rem;
  background-color: #fff;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8' fill='none'%3E%3Cpath d='M1 1.5L6 6.5L11 1.5' stroke='%2364748b' stroke-width='1.75' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 0.75rem center;
  background-size: 0.75rem;
  color: #0f172a;
  font-size: 0.9rem;
  font-weight: 500;
  line-height: 1.35;
  cursor: pointer;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}

.database-connections-page :deep(.form-select:hover),
.database-connections-page .form-select:hover {
  border-color: #94a3b8;
}

.database-connections-page :deep(.form-select:focus),
.database-connections-page .form-select:focus {
  outline: none;
  border-color: var(--p-accent, #1e3a5f);
  box-shadow: 0 0 0 3px color-mix(in srgb, var(--p-accent, #1e3a5f) 18%, transparent);
}

.database-connections-page :deep(.form-select:disabled),
.database-connections-page .form-select:disabled {
  background-color: #f8fafc;
  color: #94a3b8;
  cursor: not-allowed;
}

.database-connections-page :deep(.form-control),
.database-connections-page .form-control {
  display: block;
  width: 100%;
  padding: 0.55rem 0.75rem;
  border: 1px solid #cbd5e1;
  border-radius: 0.55rem;
  background: #fff;
  color: #0f172a;
  font-size: 0.9rem;
  line-height: 1.4;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}

.database-connections-page :deep(.form-control:focus),
.database-connections-page .form-control:focus {
  outline: none;
  border-color: var(--p-accent, #1e3a5f);
  box-shadow: 0 0 0 3px color-mix(in srgb, var(--p-accent, #1e3a5f) 18%, transparent);
}

.database-connections-page :deep(.form-label),
.database-connections-page .form-label {
  display: block;
  margin-bottom: 0.35rem;
  font-size: 0.8rem;
  font-weight: 650;
  color: #475569;
}

.database-connections-page :deep(.form-control-sm),
.database-connections-page .form-control-sm {
  padding: 0.35rem 0.55rem;
  font-size: 0.8rem;
}

.page-tabs {
  display: flex;
  flex-wrap: wrap;
  gap: 0.25rem;
  margin-bottom: 1.25rem;
  padding-bottom: 0.75rem;
  border-bottom: 1px solid var(--p-border, #e2e8f0);
}

.page-tab {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.45rem 0.75rem;
  border: none;
  border-radius: 0.5rem;
  background: transparent;
  color: var(--p-ink-muted, #64748b);
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
}

.page-tab:hover {
  background: var(--p-surface-muted, #f1f5f9);
  color: var(--p-ink, #1e293b);
}

.page-tab.active {
  background: var(--p-accent-subtle, #eef2f8);
  color: var(--p-accent, #1e3a5f);
}

.page-tab i {
  font-size: 0.85rem;
}

.tab-content {
  min-height: 240px;
}

.tab-pane {
  animation: fadeIn 0.2s ease-in-out;
}

@keyframes fadeIn {
  from { opacity: 0; transform: translateY(4px); }
  to { opacity: 1; transform: translateY(0); }
}

.empty-hint {
  margin: 0.5rem 0 0;
  font-size: 0.875rem;
  color: var(--p-ink-muted, #64748b);
}

.entity-card--error {
  border-left-color: #dc2626;
}

.connection-card .entity-card-meta {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.35rem 0.75rem;
}

.connection-actions {
  position: relative;
}

.connection-actions .more-btn {
  padding-left: 0.55rem;
  padding-right: 0.55rem;
}

.connection-actions .dropdown {
  position: relative;
}

.connection-actions .dropdown-menu {
  display: none;
  position: absolute;
  right: 0;
  bottom: calc(100% + 0.35rem);
  min-width: 11rem;
  margin: 0;
  padding: 0.35rem 0;
  list-style: none;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 0.5rem;
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.12);
  z-index: 20;
}

.connection-actions .dropdown-menu.show,
.connection-actions .dropdown.show .dropdown-menu {
  display: block;
}

.connection-actions .dropdown-item {
  display: flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.45rem 0.85rem;
  font-size: 0.85rem;
  color: #334155;
  cursor: pointer;
  text-decoration: none;
}

.connection-actions .dropdown-item:hover {
  background: #f8fafc;
}

.connection-actions .dropdown-item.text-danger {
  color: #b91c1c;
}

.connection-actions .dropdown-divider {
  margin: 0.25rem 0;
  border: none;
  border-top: 1px solid #e2e8f0;
}

/* Tab panels */
.query-builder-section,
.duplicate-analysis-section,
.schema-explorer-section {
  background: #fff;
  border: 1px solid var(--p-border, #e2e8f0);
  border-radius: 0.75rem;
  padding: 1.25rem 1.35rem;
  box-shadow: none;
}

.quick-access-guide {
  margin-top: 1rem;
  padding: 0.75rem;
  background-color: #f8fafc;
  border-radius: 0.5rem;
  border-left: 3px solid var(--p-accent, #1e3a5f);
}

.quick-access-guide strong {
  color: var(--p-accent, #1e3a5f);
}

.query-builder-section h3,
.duplicate-analysis-section h3,
.schema-explorer-section h3 {
  color: var(--p-ink, #1e293b);
  margin: 0 0 0.5rem;
  font-size: 1.1rem;
}

.query-builder-section p,
.duplicate-analysis-section p,
.schema-explorer-section p {
  color: var(--p-ink-muted, #64748b);
  margin: 0 0 1.25rem;
  font-size: 0.9rem;
}

/* Dropdown Styles */
.dropdown {
  position: relative;
}

.dropdown-menu {
  display: none;
  position: absolute;
  top: 100%;
  right: 0;
  z-index: 1000;
  min-width: 160px;
  padding: 0.5rem 0;
  margin: 0.125rem 0 0;
  background-color: #fff;
  border: 1px solid rgba(0,0,0,.15);
  border-radius: 0.375rem;
  box-shadow: 0 0.5rem 1rem rgba(0,0,0,.175);
}

.dropdown-menu.show {
  display: block;
}

.dropdown-item {
  display: block;
  width: 100%;
  padding: 0.375rem 1rem;
  clear: both;
  font-weight: 400;
  color: #212529;
  text-align: inherit;
  text-decoration: none;
  white-space: nowrap;
  background-color: transparent;
  border: 0;
  cursor: pointer;
}

.dropdown-item:hover {
  color: #1e2125;
  background-color: #e9ecef;
}

.dropdown-item.text-danger:hover {
  color: #fff;
  background-color: #dc3545;
}

.dropdown-divider {
  height: 0;
  margin: 0.5rem 0;
  overflow: hidden;
  border-top: 1px solid rgba(0,0,0,.15);
}

.empty-state {
  text-align: center;
  padding: 4rem 2rem;
}

.empty-state-content {
  max-width: 500px;
  margin: 0 auto;
}

.empty-state-icon {
  font-size: 4rem;
  color: #6c757d;
  margin-bottom: 1.5rem;
}

.empty-state h3 {
  color: #2c3e50;
  margin-bottom: 1rem;
}

.empty-state p {
  color: #6c757d;
  margin-bottom: 2rem;
  line-height: 1.6;
}

.modal-xl {
  max-width: 1200px;
}

.results-container {
  border: 1px solid #dee2e6;
  border-radius: 8px;
  overflow: hidden;
}

.results-info {
  padding: 0.5rem 1rem;
  background-color: #f8f9fa;
  border-bottom: 1px solid #dee2e6;
}

.query-results .table {
  margin-bottom: 0;
}

.font-monospace {
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
}

/* Schema Modal Styles */
.schema-overview {
  background: #f8f9fa;
  padding: 1.5rem;
  border-radius: 8px;
  margin-bottom: 1rem;
}

.stat-card {
  text-align: center;
  padding: 1rem;
  background: white;
  border-radius: 6px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.1);
}

.stat-card .stat-value {
  font-size: 1.5rem;
  font-weight: 700;
  color: #2c3e50;
  display: block;
}

.stat-card .stat-label {
  font-size: 0.75rem;
  color: #6c757d;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-top: 0.25rem;
}

.entity-suggestions {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.suggestion-card {
  border: 1px solid #dee2e6;
  border-radius: 8px;
  padding: 1rem;
  background: white;
}

.suggestion-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 0.75rem;
}

.suggestion-table {
  font-size: 1.1rem;
  font-weight: 600;
  color: #2c3e50;
  margin-bottom: 0.25rem;
}

.suggestion-type {
  font-size: 0.75rem;
  margin-right: 0.5rem;
}

.confidence-score {
  font-size: 0.8rem;
  color: #6c757d;
}

.columns-list {
  display: flex;
  flex-wrap: wrap;
  gap: 0.25rem;
  margin-top: 0.5rem;
}

.column-badge {
  display: inline-block;
  padding: 0.25rem 0.5rem;
  background: #e9ecef;
  border-radius: 4px;
  font-size: 0.75rem;
  color: #495057;
}

.column-badge.primary-key {
  background: #ffd700;
  color: #856404;
  font-weight: 600;
}

.column-badge small {
  color: #6c757d;
  margin-left: 0.25rem;
}

.tables-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 1rem;
}

.table-card {
  border: 1px solid #dee2e6;
  border-radius: 6px;
  padding: 1rem;
  background: white;
}

.table-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 0.75rem;
}

.table-name {
  font-size: 1rem;
  font-weight: 600;
  color: #2c3e50;
  margin-bottom: 0.25rem;
}

.table-stats {
  display: flex;
  gap: 0.25rem;
  flex-wrap: wrap;
}

.table-actions {
  display: flex;
  gap: 0.5rem;
}

.patterns-list {
  background: #f8f9fa;
  padding: 1rem;
  border-radius: 6px;
}

.pattern-item {
  padding: 0.5rem 0;
  border-bottom: 1px solid #e9ecef;
  font-size: 0.9rem;
}

.pattern-item:last-child {
  border-bottom: none;
}

/* Enhanced Query Results */
.query-results {
  margin-top: 1.5rem;
}

.results-header {
  border-bottom: 2px solid #e9ecef;
  padding-bottom: 0.75rem;
}

.results-actions {
  display: flex;
  gap: 0.5rem;
}

.results-info {
  background: #f8f9fa;
  padding: 0.75rem 1rem;
  border-radius: 6px;
  border: 1px solid #e9ecef;
}

.kpi-container {
  display: flex;
  align-items: center;
  justify-content: space-around;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.kpi-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  min-width: 80px;
}

.kpi-value {
  font-size: 1.1rem;
  font-weight: 700;
  color: #2c3e50;
  line-height: 1.2;
}

.kpi-label {
  font-size: 0.7rem;
  color: #6c757d;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-top: 0.1rem;
}

.kpi-divider {
  width: 1px;
  height: 30px;
  background: #dee2e6;
  margin: 0 0.5rem;
}

/* Table Container with Horizontal Scroll */
.table-container {
  border: 1px solid #dee2e6;
  border-radius: 6px;
  overflow: hidden;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  background: white;
}

.table-scroll-wrapper {
  overflow-x: auto;
  overflow-y: auto;
  max-height: 500px;
  position: relative;
}

.data-table {
  margin-bottom: 0;
  font-size: 0.85rem;
  min-width: 100%;
  table-layout: auto;
}

/* Sticky row number column */
.sticky-column {
  position: sticky;
  left: 0;
  z-index: 10;
  background: #f8f9fa !important;
  border-right: 2px solid #dee2e6 !important;
  min-width: 50px;
  max-width: 50px;
  width: 50px;
}

.scroll-indicator {
  text-align: center;
  padding: 0.5rem;
  background: #f8f9fa;
  border-top: 1px solid #dee2e6;
  color: #6c757d;
}

/* Table Header Styles */
.data-table th {
  background: #2c3e50;
  color: white;
  font-weight: 600;
  border: none;
  position: sticky;
  top: 0;
  z-index: 5;
  padding: 0.75rem 0.75rem;
  white-space: nowrap;
  min-width: 120px;
}

.data-table th.sticky-column {
  z-index: 15;
  background: #34495e !important;
  text-align: center;
  min-width: 50px;
  max-width: 50px;
  width: 50px;
}

.sortable-header {
  cursor: pointer;
  user-select: none;
  transition: background-color 0.2s ease;
}

.sortable-header:hover {
  background: #34495e !important;
}

.header-content {
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-width: 100px;
}

.column-name {
  font-weight: 600;
  margin-right: 0.5rem;
}

/* Table Body Styles */
.data-table td {
  padding: 0.5rem 0.75rem;
  border-top: 1px solid #dee2e6;
  vertical-align: top;
  min-width: 120px;
  max-width: 250px;
}

.data-table td.sticky-column {
  background: #f8f9fa !important;
  font-weight: 600;
  text-align: center;
  color: #6c757d;
  border-right: 2px solid #dee2e6 !important;
  min-width: 50px;
  max-width: 50px;
  width: 50px;
}

.data-cell {
  position: relative;
}

.cell-content {
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 220px;
}

/* Row Hover Effects */
.data-table tbody tr:hover {
  background-color: #f8f9fa;
}

.data-table tbody tr:hover .sticky-column {
  background-color: #e9ecef !important;
}

/* Scrollbar Styling */
.table-scroll-wrapper::-webkit-scrollbar {
  height: 8px;
  width: 8px;
}

.table-scroll-wrapper::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 4px;
}

.table-scroll-wrapper::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 4px;
}

.table-scroll-wrapper::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}

.error-header {
  display: flex;
  align-items: center;
}

.error-details {
  margin-left: 1.5rem;
}

.results-pagination {
  border-top: 1px solid #e9ecef;
  padding-top: 1rem;
}

@media (max-width: 768px) {
  .list-header {
    flex-direction: column;
  }

  .connection-card .entity-card-meta {
    grid-template-columns: 1fr;
  }
  
  .tables-grid {
    grid-template-columns: 1fr;
  }
  
  .suggestion-header {
    flex-direction: column;
    gap: 0.5rem;
  }
  
  .stat-card {
    margin-bottom: 0.5rem;
  }
  
  /* Mobile KPI Layout */
  .kpi-container {
    justify-content: space-between;
    padding: 0.5rem 0;
  }
  
  .kpi-item {
    min-width: 60px;
  }
  
  .kpi-value {
    font-size: 1rem;
  }
  
  .kpi-label {
    font-size: 0.65rem;
  }
  
  .kpi-divider {
    height: 25px;
    margin: 0 0.25rem;
  }
  
  /* Mobile Table Adjustments */
  .data-table th,
  .data-table td {
    min-width: 100px;
    padding: 0.5rem;
  }
  
  .cell-content {
    max-width: 150px;
  }
  
  .header-content {
    min-width: 80px;
  }
  
  .results-actions {
    flex-direction: column;
    gap: 0.25rem;
  }
  
  .results-actions .btn {
    font-size: 0.8rem;
    padding: 0.375rem 0.75rem;
  }
}

/* Query Section Styles */
.query-section {
  background: #ffffff;
  border: 1px solid #e9ecef;
  border-radius: 12px;
  padding: 1.5rem;
  margin-bottom: 2rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  transition: all 0.3s ease;
}

.query-section:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
}

/* Section-specific styling */
.ai-query-section {
  border-left: 4px solid #007bff;
  background: linear-gradient(135deg, #f8f9ff 0%, #ffffff 100%);
}

.business-intelligence-section {
  border-left: 4px solid #28a745;
  background: linear-gradient(135deg, #f0fff4 0%, #ffffff 100%);
}

.duplicate-detection-section {
  border-left: 4px solid #ffc107;
  background: linear-gradient(135deg, #fffbf0 0%, #ffffff 100%);
}

.manual-query-section {
  border-left: 4px solid #6c757d;
  background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%);
}

.section-header {
  margin-bottom: 1.5rem;
  padding-bottom: 0.75rem;
  border-bottom: 1px solid #e9ecef;
}

.section-title {
  color: #2c3e50;
  font-weight: 600;
  margin-bottom: 0.5rem;
  font-size: 1.1rem;
}

.section-description {
  color: #6c757d;
  font-size: 0.9rem;
  margin: 0;
  line-height: 1.4;
}

.quick-actions .btn-group {
  gap: 0.5rem;
}

.quick-actions .btn {
  border-radius: 6px;
  font-size: 0.8rem;
  padding: 0.375rem 0.75rem;
  transition: all 0.2s ease;
}

.quick-actions .btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.business-suggestions {
  margin-top: 1rem;
}

.suggestions-list {
  max-height: 200px;
  overflow-y: auto;
  border: 1px solid #dee2e6;
  border-radius: 6px;
  background: white;
}

.suggestion-item {
  padding: 0.75rem;
  border-bottom: 1px solid #f1f3f4;
  cursor: pointer;
  transition: background-color 0.2s ease;
}

.suggestion-item:hover {
  background-color: #f8f9fa;
}

.suggestion-item:last-child {
  border-bottom: none;
}

.suggestion-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.25rem;
}

.suggestion-description {
  font-size: 0.9rem;
  color: #495057;
  margin-bottom: 0.25rem;
}

.suggestion-value {
  font-size: 0.8rem;
}

/* Enhanced suggestion actions */
.suggestion-actions {
  display: flex;
  gap: 0.5rem;
  margin-top: 0.5rem;
}

.suggestion-actions .btn {
  font-size: 0.75rem;
  padding: 0.25rem 0.5rem;
}

/* Duplicate Detection Styles */
.duplicate-form {
  margin-top: 1rem;
}

.duplicate-form .input-group {
  gap: 0.5rem;
}

.duplicate-results {
  background: white;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  padding: 1rem;
}

.duplicate-summary {
  display: flex;
  gap: 1rem;
  margin-bottom: 1rem;
}

.summary-card {
  background: #f8f9fa;
  border-radius: 6px;
  padding: 1rem;
  text-align: center;
  flex: 1;
}

.summary-value {
  font-size: 1.5rem;
  font-weight: bold;
  color: #007bff;
}

.summary-label {
  font-size: 0.8rem;
  color: #6c757d;
  margin-top: 0.25rem;
}

.duplicates-section {
  margin: 1rem 0;
  padding: 1rem;
  background: #fff5f5;
  border-left: 4px solid #dc3545;
  border-radius: 4px;
}

.duplicate-item {
  background: white;
  border: 1px solid #f8d7da;
  border-radius: 4px;
  padding: 0.75rem;
  margin: 0.5rem 0;
}

.duplicate-item.high-confidence {
  border-color: #dc3545;
  background: #f8d7da;
}

.recommendations-section {
  margin: 1rem 0;
  padding: 1rem;
  background: #f0f8ff;
  border-left: 4px solid #007bff;
  border-radius: 4px;
}

.recommendations-list {
  margin: 0.5rem 0;
  padding-left: 1.5rem;
}

.analysis-text {
  margin: 1rem 0;
  line-height: 1.5;
}

/* AI-generated query indicator */
.form-text .text-success {
  font-weight: 500;
}

/* Table actions with AI button */
.table-actions {
  display: flex;
  gap: 0.25rem;
  flex-wrap: wrap;
}

.table-actions .btn {
  font-size: 0.75rem;
  padding: 0.25rem 0.5rem;
}

@media (max-width: 768px) {
  .query-section {
    padding: 1rem;
    margin-bottom: 1.5rem;
  }
  
  .section-header {
    margin-bottom: 1rem;
  }
  
  .section-title {
    font-size: 1rem;
  }
  
  .quick-actions .btn-group {
    flex-direction: column;
    width: 100%;
  }
  
  .quick-actions .btn {
    width: 100%;
    margin-bottom: 0.25rem;
  }
  
  .suggestion-actions {
    flex-direction: column;
    gap: 0.25rem;
  }
  
  .table-actions {
    justify-content: center;
  }
  
  .duplicate-form .input-group {
    flex-direction: column;
  }
  
  .duplicate-form .input-group > * {
    margin-bottom: 0.5rem;
  }
}
.ls-1 {
  letter-spacing: 0.1em;
}

.bg-danger-light {
  background-color: rgba(220, 53, 69, 0.1);
}

.bg-primary-soft {
  background-color: rgba(13, 110, 253, 0.1);
}

.bg-success-soft {
  background-color: rgba(25, 135, 84, 0.1);
}

.bg-light-soft {
  background-color: rgba(248, 249, 250, 0.5);
}

.line-height-base {
  line-height: 1.6;
}

.whitespace-pre-wrap {
  white-space: pre-wrap;
}

.italic {
  font-style: italic;
}

.opacity-10 {
  opacity: 0.1;
}

.opacity-20 {
  opacity: 0.2;
}

.border-2 {
  border-width: 2px !important;
}

.border-3 {
  border-width: 3px !important;
}

.upload-zone {
  transition: all 0.2s ease;
}

.upload-zone:hover:not(.opacity-50) {
  border-color: #0d6efd !important;
  background-color: rgba(13, 110, 253, 0.05) !important;
}

.relationship-badge {
  transition: all 0.2s ease;
}

.relationship-badge:hover {
  background-color: #fff !important;
  transform: translateX(5px);
  box-shadow: 0 2px 5px rgba(0,0,0,0.05);
}

.accordion-button:not(.collapsed) {
  background-color: #f8f9fa;
  color: #0d6efd;
  box-shadow: none;
}

.knowledge-interface .card {
  transition: transform 0.3s ease, box-shadow 0.3s ease;
}

/* Custom scrollbar for knowledge base */
.knowledge-content::-webkit-scrollbar {
  width: 6px;
}

.knowledge-content::-webkit-scrollbar-track {
  background: #f1f1f1;
}

.knowledge-content::-webkit-scrollbar-thumb {
  background: #ccc;
  border-radius: 10px;
}

.knowledge-content::-webkit-scrollbar-thumb:hover {
  background: #999;
}

/* Knowledge Summary Card */
.knowledge-summary-card {
  background: linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%);
  border: 1px solid #bbf7d0;
  border-radius: 12px;
  padding: 1.5rem;
}

.knowledge-icon-wrapper {
  width: 56px;
  height: 56px;
  background: linear-gradient(135deg, #22c55e 0%, #16a34a 100%);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 1rem;
}

.knowledge-icon-wrapper i {
  font-size: 1.5rem;
  color: white;
}

.knowledge-stats {
  display: flex;
  gap: 1.5rem;
}

.stat-item {
  text-align: center;
}

.stat-value {
  display: block;
  font-size: 1.75rem;
  font-weight: 700;
  color: #16a34a;
  line-height: 1;
}

.stat-label {
  display: block;
  font-size: 0.75rem;
  color: #6b7280;
  text-transform: uppercase;
  margin-top: 0.25rem;
}

.extraction-info {
  border-color: #bbf7d0 !important;
}

.cursor-pointer {
  cursor: pointer;
}

.cursor-pointer:hover {
  opacity: 0.8;
}
</style>

<!-- Non-scoped styles for teleported modals -->
<style>
/* Create Modal Styles */
.create-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  padding: 1rem;
  backdrop-filter: blur(4px);
}

.create-modal-container {
  background: #fff;
  border-radius: 12px;
  width: 100%;
  max-width: 640px;
  max-height: 90vh;
  overflow: hidden;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  animation: createModalSlideIn 0.2s ease-out;
}

@keyframes createModalSlideIn {
  from {
    opacity: 0;
    transform: translateY(-20px) scale(0.95);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

.create-modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1.25rem 1.5rem;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
}

.create-modal-title {
  font-size: 1.25rem;
  font-weight: 600;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.create-modal-title i {
  font-size: 1.1rem;
}

.create-modal-close {
  background: rgba(255, 255, 255, 0.2);
  border: none;
  color: white;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.2s;
}

.create-modal-close:hover {
  background: rgba(255, 255, 255, 0.3);
}

.create-modal-body {
  padding: 1.5rem;
  overflow-y: auto;
  max-height: calc(90vh - 160px);
}

.create-form-row {
  display: flex;
  gap: 1rem;
  margin-bottom: 1rem;
}

.create-form-group {
  margin-bottom: 1rem;
}

.create-form-group.create-flex-1 {
  flex: 1;
}

.create-form-group.create-flex-2 {
  flex: 2;
}

.create-form-label {
  display: block;
  font-size: 0.875rem;
  font-weight: 600;
  color: #374151;
  margin-bottom: 0.5rem;
}

.create-required {
  color: #ef4444;
}

.create-optional {
  font-weight: 400;
  color: #9ca3af;
  font-size: 0.8rem;
}

.create-form-input,
.create-form-select,
.create-form-textarea {
  width: 100%;
  padding: 0.625rem 0.875rem;
  border: 1px solid #cbd5e1;
  border-radius: 0.55rem;
  font-size: 0.9375rem;
  color: #0f172a;
  background: #fff;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}

.create-form-select {
  appearance: none;
  -webkit-appearance: none;
  padding-right: 2.25rem;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8' fill='none'%3E%3Cpath d='M1 1.5L6 6.5L11 1.5' stroke='%2364748b' stroke-width='1.75' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 0.75rem center;
  background-size: 0.75rem;
  cursor: pointer;
}

.create-form-input:focus,
.create-form-select:focus,
.create-form-textarea:focus {
  outline: none;
  border-color: var(--p-accent, #1e3a5f);
  box-shadow: 0 0 0 3px color-mix(in srgb, var(--p-accent, #1e3a5f) 18%, transparent);
}

.create-form-input::placeholder,
.create-form-textarea::placeholder {
  color: #9ca3af;
}

.create-form-textarea {
  resize: vertical;
  min-height: 60px;
}

.create-form-hint {
  display: block;
  font-size: 0.8rem;
  color: #6b7280;
  margin-top: 0.375rem;
}

.create-password-group {
  display: flex;
  position: relative;
}

.create-password-input {
  padding-right: 3rem;
}

.create-password-toggle {
  position: absolute;
  right: 0;
  top: 0;
  bottom: 0;
  width: 2.75rem;
  background: transparent;
  border: none;
  color: #6b7280;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: color 0.2s;
}

.create-password-toggle:hover {
  color: #374151;
}

.create-checkbox-label {
  display: flex;
  align-items: center;
  cursor: pointer;
  gap: 0.625rem;
}

.create-checkbox {
  width: 18px;
  height: 18px;
  accent-color: #667eea;
  cursor: pointer;
}

.create-checkbox-text {
  font-size: 0.9375rem;
  color: #374151;
}

.create-modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  padding: 1rem 1.5rem;
  background: #f9fafb;
  border-top: 1px solid #e5e7eb;
}

.create-btn-cancel {
  padding: 0.625rem 1.25rem;
  border: 1px solid #d1d5db;
  background: #fff;
  color: #374151;
  font-size: 0.9375rem;
  font-weight: 500;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s;
}

.create-btn-cancel:hover {
  background: #f3f4f6;
  border-color: #9ca3af;
}

.create-btn-save {
  padding: 0.625rem 1.5rem;
  border: none;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  font-size: 0.9375rem;
  font-weight: 500;
  border-radius: 8px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  transition: all 0.2s;
}

.create-btn-save:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
}

.create-btn-save:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.create-btn-save i {
  font-size: 0.875rem;
}

/* Edit Modal Styles */
.edit-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  padding: 1rem;
  backdrop-filter: blur(4px);
}

.edit-modal-container {
  background: #fff;
  border-radius: 12px;
  width: 100%;
  max-width: 640px;
  max-height: 90vh;
  overflow: hidden;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  animation: editModalSlideIn 0.2s ease-out;
}

@keyframes editModalSlideIn {
  from {
    opacity: 0;
    transform: translateY(-20px) scale(0.95);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

.edit-modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1.25rem 1.5rem;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
}

.edit-modal-title {
  font-size: 1.25rem;
  font-weight: 600;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.edit-modal-title i {
  font-size: 1.1rem;
}

.edit-modal-close {
  background: rgba(255, 255, 255, 0.2);
  border: none;
  color: white;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.2s;
}

.edit-modal-close:hover {
  background: rgba(255, 255, 255, 0.3);
}

.edit-modal-body {
  padding: 1.5rem;
  overflow-y: auto;
  max-height: calc(90vh - 160px);
}

.edit-form-row {
  display: flex;
  gap: 1rem;
  margin-bottom: 1rem;
}

.edit-form-group {
  margin-bottom: 1rem;
}

.edit-form-group.edit-flex-1 {
  flex: 1;
}

.edit-form-group.edit-flex-2 {
  flex: 2;
}

.edit-form-label {
  display: block;
  font-size: 0.875rem;
  font-weight: 600;
  color: #374151;
  margin-bottom: 0.5rem;
}

.edit-required {
  color: #ef4444;
}

.edit-optional {
  font-weight: 400;
  color: #9ca3af;
  font-size: 0.8rem;
}

.edit-form-input,
.edit-form-select,
.edit-form-textarea {
  width: 100%;
  padding: 0.625rem 0.875rem;
  border: 1px solid #cbd5e1;
  border-radius: 0.55rem;
  font-size: 0.9375rem;
  color: #0f172a;
  background: #fff;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}

.edit-form-select {
  appearance: none;
  -webkit-appearance: none;
  padding-right: 2.25rem;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8' fill='none'%3E%3Cpath d='M1 1.5L6 6.5L11 1.5' stroke='%2364748b' stroke-width='1.75' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 0.75rem center;
  background-size: 0.75rem;
  cursor: pointer;
}

.edit-form-input:focus,
.edit-form-select:focus,
.edit-form-textarea:focus {
  outline: none;
  border-color: var(--p-accent, #1e3a5f);
  box-shadow: 0 0 0 3px color-mix(in srgb, var(--p-accent, #1e3a5f) 18%, transparent);
}

.edit-form-input::placeholder,
.edit-form-textarea::placeholder {
  color: #9ca3af;
}

.edit-form-textarea {
  resize: vertical;
  min-height: 60px;
}

.edit-form-hint {
  display: block;
  font-size: 0.8rem;
  color: #6b7280;
  margin-top: 0.375rem;
}

.edit-password-group {
  display: flex;
  position: relative;
}

.edit-password-input {
  padding-right: 3rem;
}

.edit-password-toggle {
  position: absolute;
  right: 0;
  top: 0;
  bottom: 0;
  width: 2.75rem;
  background: transparent;
  border: none;
  color: #6b7280;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: color 0.2s;
}

.edit-password-toggle:hover {
  color: #374151;
}

.edit-checkbox-label {
  display: flex;
  align-items: center;
  cursor: pointer;
  gap: 0.625rem;
}

.edit-checkbox {
  width: 18px;
  height: 18px;
  accent-color: #667eea;
  cursor: pointer;
}

.edit-checkbox-text {
  font-size: 0.9375rem;
  color: #374151;
}

.edit-modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  padding: 1rem 1.5rem;
  background: #f9fafb;
  border-top: 1px solid #e5e7eb;
}

.edit-btn-cancel {
  padding: 0.625rem 1.25rem;
  border: 1px solid #d1d5db;
  background: #fff;
  color: #374151;
  font-size: 0.9375rem;
  font-weight: 500;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s;
}

.edit-btn-cancel:hover {
  background: #f3f4f6;
  border-color: #9ca3af;
}

.edit-btn-save {
  padding: 0.625rem 1.5rem;
  border: none;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  font-size: 0.9375rem;
  font-weight: 500;
  border-radius: 8px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  transition: all 0.2s;
}

.edit-btn-save:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
}

.edit-btn-save:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.edit-btn-save i {
  font-size: 0.875rem;
}

/* Responsive adjustments */
@media (max-width: 640px) {
  .create-modal-container,
  .edit-modal-container {
    max-width: 100%;
    margin: 0.5rem;
    max-height: calc(100vh - 2rem);
  }
  
  .create-form-row,
  .edit-form-row {
    flex-direction: column;
    gap: 0;
  }
  
  .create-form-group.create-flex-1,
  .create-form-group.create-flex-2,
  .edit-form-group.edit-flex-1,
  .edit-form-group.edit-flex-2 {
    flex: none;
  }
}
</style>




